/**
 * Utility function to get variant configuration from consolidated variantMetafields
 * @param {string|number} variantId - The variant ID to look up
 * @returns {Object|null} Variant configuration object or null if not found
 */
function getVariantConfig(variantId) {
  const meta = window.variantMetafields?.[variantId];
  if (!meta) return null;

  return {
    // Direct values from metafields
    available: meta.available,
    inventoryPolicy: meta.inventory_policy,
    inventoryQuantity: meta.inventory_quantity,
    inventoryManagement: meta.inventory_management,
    isMtoMetafield: meta.is_mto === true,
    showPersonalizeProduct: meta.show_personalize_product,
    showVariantDescription: meta.show_variant_description,

    // Computed properties
    continueSellingWhenOOS: meta.inventory_policy === 'continue',
    isTrackedByShopify: meta.inventory_management === 'shopify',
    isOutOfStock: meta.inventory_quantity <= 0,

    /**
     * Determines if variant is Made-To-Order
     * MTO = ALL conditions must be true:
     *   1. is_mto metafield is explicitly set to true
     *   2. inventory_quantity <= 0 (out of stock)
     *   3. available === true (can still be purchased)
     *   4. inventory_policy === 'continue' (continue selling when OOS is checked)
     */
    get isMTO() {
      return (
        meta.is_mto === true &&
        meta.inventory_quantity <= 0 &&
        meta.available === true &&
        meta.inventory_policy === 'continue'
      );
    }
  };
}

// Make it globally available
window.getVariantConfig = getVariantConfig;

/**
 * ============================================================================
 * MTO MESSAGE STATE MANAGER
 * ============================================================================
 *
 * Centralized control for Made-To-Order message display.
 * Manages state transitions and UI updates for MTO messages.
 *
 * STATES:
 *   - NO_SELECTION: No variant selected ("Select a Size")
 *   - STANDARD_NON_MTO: Variant selected, not MTO
 *   - STANDARD_MTO: Variant selected, is MTO
 *   - CUSTOM_SIZE_ACTIVE: Custom size mode active
 *
 * USAGE:
 *   MTOMessageManager.init()                    - Initialize on page load
 *   MTOMessageManager.updateForVariant(id)     - Update based on variant
 *   MTOMessageManager.reset()                  - Reset to NO_SELECTION
 *   MTOMessageManager.setCustomSizeActive()    - Enter custom size mode
 *
 * @singleton
 */
const MTOMessageManager = {
  // ─────────────────────────────────────────────────────────────────────────
  // STATE CONSTANTS
  // ─────────────────────────────────────────────────────────────────────────
  STATES: {
    NO_SELECTION: 'NO_SELECTION',
    STANDARD_NON_MTO: 'STANDARD_NON_MTO',
    STANDARD_MTO: 'STANDARD_MTO',
    CUSTOM_SIZE_ACTIVE: 'CUSTOM_SIZE_ACTIVE'
  },

  // ─────────────────────────────────────────────────────────────────────────
  // INTERNAL STATE
  // ─────────────────────────────────────────────────────────────────────────
  _currentState: null,
  _lastVariantId: null,
  _initialized: false,

  // DOM element cache (will be populated on init)
  _elements: {
    standardMessage: null,
    customMessage: null,
    dropdown: null
  },

  // ─────────────────────────────────────────────────────────────────────────
  // INITIALIZATION
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Initialize the manager - call once on page load
   * Caches DOM elements and sets initial state
   */
  init() {
    this._cacheElements();
    this._currentState = this.STATES.NO_SELECTION;
    this._initialized = true;
    console.log('[MTOManager] Initialized');
  },

  /**
   * Cache DOM element references
   * @private
   */
  _cacheElements() {
    this._elements.standardMessage = document.querySelector('.mto-message--standard');
    this._elements.customMessage = document.querySelector('.mto-message--custom');
    this._elements.dropdown = document.querySelector('.custom-dropdown-button-text');
  },

  /**
   * Re-query DOM elements (call after AJAX updates DOM)
   * @private
   */
  _refreshElements() {
    this._elements.standardMessage = document.querySelector('.mto-message--standard');
    this._elements.customMessage = document.querySelector('.mto-message--custom');
    this._elements.dropdown = document.querySelector('.custom-dropdown-button-text');
  },

  // ─────────────────────────────────────────────────────────────────────────
  // STATE DETERMINATION
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Determine current state based on UI and variant
   *
   * Priority order:
   *   1. Custom size mode (highest)
   *   2. No selection
   *   3. MTO variant
   *   4. Non-MTO variant
   *
   * @param {string|number|null} variantId - The currently selected variant ID
   * @returns {string} The determined state constant
   */
  determineState(variantId = null) {
    // Priority 1: Check custom size mode (highest priority)
    const isCustomSizeActive =
      localStorage.getItem('customSize') === 'active' ||
      document.querySelector('.custom-size.active') !== null;

    if (isCustomSizeActive) {
      return this.STATES.CUSTOM_SIZE_ACTIVE;
    }

    // Priority 2: Check if NO variant is selected
    const dropdownText = this._elements.dropdown?.textContent?.trim();
    const isNoSelection = !variantId || dropdownText === 'Select a Size';

    if (isNoSelection) {
      return this.STATES.NO_SELECTION;
    }

    // Priority 3 & 4: Check variant MTO status
    const variantConfig = window.getVariantConfig(variantId);

    if (variantConfig && variantConfig.isMTO) {
      return this.STATES.STANDARD_MTO;
    }

    return this.STATES.STANDARD_NON_MTO;
  },

  // ─────────────────────────────────────────────────────────────────────────
  // PUBLIC API
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Update state and UI based on variant selection
   * This is the PRIMARY method to call after any variant change
   *
   * @param {string|number|null} variantId - The currently selected variant ID
   */
  updateForVariant(variantId = null) {
    if (!this._initialized) {
      console.warn('[MTOManager] Not initialized, calling init()');
      this.init();
    }

    // Refresh DOM elements in case AJAX updated the DOM
    this._refreshElements();

    const newState = this.determineState(variantId);

    // Optimization: Skip if state hasn't changed
    if (newState === this._currentState && variantId === this._lastVariantId) {
      console.log('[MTOManager] State unchanged, skipping UI update');
      return;
    }

    console.log('[MTOManager] State transition:', this._currentState, '→', newState,
                '| Variant:', variantId);

    this._currentState = newState;
    this._lastVariantId = variantId;
    this._updateUI();
  },

  /**
   * Force state to NO_SELECTION
   * Use when: height changes, category changes, dropdown resets to "Select a Size"
   */
  reset() {
    if (!this._initialized) {
      this.init();
    }

    console.log('[MTOManager] Reset to NO_SELECTION');
    this._currentState = this.STATES.NO_SELECTION;
    this._lastVariantId = null;
    this._refreshElements();
    this._updateUI();
  },

  /**
   * Force state to CUSTOM_SIZE_ACTIVE
   * Use when: user clicks "Custom Size" option
   */
  setCustomSizeActive() {
    if (!this._initialized) {
      this.init();
    }

    console.log('[MTOManager] Set CUSTOM_SIZE_ACTIVE');
    this._currentState = this.STATES.CUSTOM_SIZE_ACTIVE;
    this._refreshElements();
    this._updateUI();
  },

  /**
   * Get current state (for debugging)
   * @returns {string} Current state constant
   */
  getCurrentState() {
    return this._currentState;
  },

  /**
   * Check if currently in custom size mode
   * @returns {boolean}
   */
  isCustomSizeMode() {
    return this._currentState === this.STATES.CUSTOM_SIZE_ACTIVE;
  },

  /**
   * Check if currently showing MTO message
   * @returns {boolean}
   */
  isShowingMTOMessage() {
    return this._currentState === this.STATES.STANDARD_MTO ||
           this._currentState === this.STATES.CUSTOM_SIZE_ACTIVE;
  },

  // ─────────────────────────────────────────────────────────────────────────
  // UI UPDATE (PRIVATE)
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Update DOM elements based on current state
   * @private
   */
  _updateUI() {
    const { standardMessage, customMessage } = this._elements;

    // Safety check - elements might not exist on some pages
    if (!standardMessage && !customMessage) {
      console.log('[MTOManager] MTO message elements not found in DOM');
      return;
    }

    // Reset both messages first
    standardMessage?.classList.add('hidden');
    customMessage?.classList.add('hidden');

    // Show appropriate message based on state
    console.log("this._currentState",this._currentState);
    switch (this._currentState) {
      case this.STATES.NO_SELECTION:
      case this.STATES.STANDARD_NON_MTO:
        // Both messages hidden (already done above)
        /******* added code by ens 26-12-25*****/
        removeCustomSizeIsMto();
        /******* added code by ens 26-12-25*****/
        break;

      case this.STATES.STANDARD_MTO:
        removeCustomSizeIsMto();
        insertCustomSizeIsMto();
        standardMessage?.classList.remove('hidden');
        break;

      case this.STATES.CUSTOM_SIZE_ACTIVE:
        /******* added code by ens 26-12-25*****/
        removeCustomSizeIsMto();
        /******* added code by ens 26-12-25*****/
        customMessage?.classList.remove('hidden');
        break;
    }

    console.log('[MTOManager] UI updated | State:', this._currentState,
                '| Standard visible:', !standardMessage?.classList.contains('hidden'),
                '| Custom visible:', !customMessage?.classList.contains('hidden'));

    function insertCustomSizeIsMto() {
        console.log("insertCustomSizeIsMto");
        const targetDiv = document.querySelector('.product-form form');
        if (targetDiv) {
          var dimension_value = localStorage.getItem("selectedSize")
          const input = document.createElement('input');
          input.type = 'hidden';
          input.className = 'mto_custom';
          input.name = 'properties[_custom_size]';
          input.value = dimension_value +'_ismto';

          targetDiv.appendChild(input);
         
          const input_is_mto= document.createElement('input');
          input_is_mto.type = 'hidden';
          input_is_mto.name = 'properties[_is_mto]';
          input_is_mto.value = '1';

          targetDiv.appendChild(input_is_mto);

        }
      }

    function removeCustomSizeIsMto() {
          console.log("removeCustomSizeIsMto");
          var productForm = document.querySelector(".product-form form");
          if (productForm) {
            // Remove _custom_size input if it exists
            const existingCustomSizeInput = productForm.querySelector('input.mto_custom[name="properties[_custom_size]"]');
            if (existingCustomSizeInput) {
              productForm.removeChild(existingCustomSizeInput);
            }
        
            // Remove _is_mto input if it exists
            const existingIsMtoInput = productForm.querySelector('input[name="properties[_is_mto]"]');
            if (existingIsMtoInput) {
              productForm.removeChild(existingIsMtoInput);
            }
          }
      }
  }

};

// Make globally available
window.MTOMessageManager = MTOMessageManager;

/**
 * ============================================================================
 * SCROLL TO PINCODE WITH HIGHLIGHT
 * ============================================================================
 *
 * Scrolls to the pincode input section and highlights it with a golden glow.
 * Called when user clicks "Check estimated timeline" link in MTO messages.
 */
function scrollToPincodeWithHighlight() {
  // 1. Find the pincode wrapper element
  const el = document.querySelector('.pincode-wrapper');
  if (!el) {
    console.log('[MTO] Pincode wrapper not found');
    return;
  }

  // 2. Smooth scroll to center the element in viewport
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });

  // 3. After scroll settles (400ms), trigger highlight animation
  setTimeout(() => {
    // Remove class first to reset animation if already present
    el.classList.remove('mto-highlight');

    // Force browser reflow - ensures animation restarts from beginning
    void el.offsetWidth;

    // Add class to trigger CSS animation
    el.classList.add('mto-highlight');
    console.log('[MTO] Pincode wrapper highlighted');
  }, 400);
}

// Single event listener with guard (prevents duplicate bindings)
if (!window._mtoClickBound) {
  window._mtoClickBound = true; // Set flag to prevent re-binding

  // Use event delegation on document
  document.addEventListener('click', (e) => {
    // Check if clicked element is or is inside .mto-check-timeline
    if (e.target.closest('.mto-check-timeline')) {
      e.preventDefault(); // Prevent default anchor behavior
      scrollToPincodeWithHighlight();
    }
  });
}

// Export globally
window.scrollToPincodeWithHighlight = scrollToPincodeWithHighlight;

if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();
        this.quantityInput = this.querySelector('.quantity__input');
      }
      
      connectedCallback() {
        console.log("connectedCallback");
        // cutom dev
        // localStorage.removeItem("selectedSize");

        // ─────────────────────────────────────────────────────────────────────
        // HOOK: Initialize MTO Message Manager
        // ─────────────────────────────────────────────────────────────────────
        MTOMessageManager.init();

        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));

        const urlParams = new URLSearchParams(window.location.search);

        const variantId = urlParams.get('variant');
      
        var stickyBarVariant = document.querySelector(".product-main-sticky-bar .product-sticky-variant");
        const customDropdownLabel = document.querySelector(".custom-dropdown-button-text");
        if(stickyBarVariant && customDropdownLabel){
          const heightOption = document.querySelector(".variant-pill-list.height");
          if(heightOption){
            customDropdownLabel.removeAttribute("data-selected");
            const spanElement = document.createElement("span");
            spanElement.textContent = 'Select a Size!';
            spanElement.style.color = 'red';
            stickyBarVariant.replaceChildren(spanElement);
            document.querySelector(".product-main-sticky-bar .sticky-addtocart-btn .btn-text").textContent = 'Select a Size';
          }
        }
         
        // TWO-PATH INITIALIZATION: Branch based on URL variant presence
        if (variantId) {
          // PATH A: Auto-select from URL variant
          console.log('[Init] Variant in URL, using auto-select path');

          // Initialize image gallery (prevents conflicts with variant changes)
          var currentVariant = document.querySelector(".cst-product-slider-wrapper .pdp-main-slider-wrap")?.getAttribute("data-seleted-alt");
          this.cstUpdateMedia(currentVariant);

          // Set up event listeners FIRST, then trigger auto-select
          this.sizeElems();
          this.sizeConverter();
          this.customSize();

          // Run auto-select after gallery is initialized
          requestAnimationFrame(() => {
            this.autoSelectVariantFromUrl()
              .then((success) => {
                // ─────────────────────────────────────────────────────────────
                // HOOK 2: After auto-select from URL completes
                // Update MTO message based on selected variant
                // ─────────────────────────────────────────────────────────────
                if (success && variantId) {
                  MTOMessageManager.updateForVariant(variantId);
                } else {
                  MTOMessageManager.reset();
                }
              })
              .catch(error => {
                console.error('[Init] Auto-select error:', error);
                MTOMessageManager.reset();
              });
          });

        } else {
          // PATH B: Normal initialization (no variant in URL)
          // Keep existing behavior exactly as is
          console.log('[Init] No variant in URL, using normal initialization');

          this.initializeDefaults();
          this.sizeElems();

          var currentVariant = document.querySelector(".cst-product-slider-wrapper .pdp-main-slider-wrap")?.getAttribute("data-seleted-alt");
          this.cstUpdateMedia(currentVariant);

          // Fallback to old logic for string-based URLs
          var heightOptions = document.querySelectorAll(".product-form__input.height .height_option:not(.hidden)");
          const currentUrl = window.location.href;
          if (heightOptions.length > 0) {
            if (currentUrl.includes("6-inch")) {
             heightOptions.forEach(heightOpt=>{
               var heightVal = heightOpt.getAttribute("data-value");
               if(heightVal == 6){
                  heightOpt.dataset.trigger = "true";
                  heightOpt.click();
               }
             })
            }else if (currentUrl.includes("8-inch")) {
              heightOptions.forEach(heightOpt=>{
                var heightVal = heightOpt.getAttribute("data-value");
                if(heightVal == 8){
                  heightOpt.dataset.trigger = "true";
                  heightOpt.click();
                }
              })
            }else if (currentUrl.includes("10-inch")) {
             heightOptions.forEach(heightOpt=>{
               var heightVal = heightOpt.getAttribute("data-value");
               if(heightVal == 10){
                  heightOpt.dataset.trigger = "true";
                  heightOpt.click();
               }
             })
            }
          }

          this.sizeConverter();
          console.log("customSize function called....");
          this.customSize();

          // ─────────────────────────────────────────────────────────────────────
          // HOOK 1: Normal page load (no URL variant)
          // Conditional initialization based on product type
          // ─────────────────────────────────────────────────────────────────────
          const heightOptionsForInit = document.querySelectorAll(".product-form__input.height .height_option");
          const hasHeightSelector = heightOptionsForInit.length > 0;

          if (hasHeightSelector) {
            // Products WITH height options: Require user selection
            console.log('[Init] Product has height options, resetting MTO state');
            MTOMessageManager.reset();
          } else {
            // Products WITHOUT height options: Variant is pre-selected on page load
            const preSelectedVariantId = document.querySelector('product-form input[name="id"]')?.value;
            if (preSelectedVariantId) {
              console.log('[Init] No height options, checking pre-selected variant:', preSelectedVariantId);
              MTOMessageManager.updateForVariant(preSelectedVariantId);
            } else {
              console.log('[Init] No height options and no pre-selected variant, resetting');
              MTOMessageManager.reset();
            }
          }
        }
      }

      optionDimensionValue(option2,option3){
          if(option2 && option2.match("x")){
            return option2
          }else if(option3 && option3.match("x")){
            return option3
          }else{
            console.log("**********option 2 and 3 has no x");
          }
      }
      onLoadFunction(){
        var _this = this;
        const selectedVariant = document.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        console.log("selectedVarianttttttttttttttttttttt",selectedVariant);
        let selectedVariantVal = null;
        if (selectedVariant){
          try {
            selectedVariantVal = JSON.parse(selectedVariant);
          } catch (error) {
            console.error("Error parsing selectedVariant HTML:", error);
          }
        }
        if(selectedVariantVal){
          var dimensionValue;
          console.log("setting all the default value");
          localStorage.setItem("selectedCat", selectedVariantVal.option1); 
          if(selectedVariantVal.option2 && selectedVariantVal.option2.match("x")){
             dimensionValue = selectedVariantVal.option2;
             localStorage.setItem("selectedSize", selectedVariantVal.option2);
             localStorage.setItem("selectedcolor", selectedVariantVal.option3);

          }else if(selectedVariantVal.option3 && selectedVariantVal.option3.match("x")){
             dimensionValue = selectedVariantVal.option3;
             localStorage.setItem("selectedSize", selectedVariantVal.option3);
             localStorage.setItem("selectedcolor", selectedVariantVal.option2);
          }else{
            console.log("no product has dimensions");
          }

          if (dimensionValue) {
            _this.heightButtonHideShowPerSize();
            const dimElem = document.querySelector(`.dimension_val[data-value="${dimensionValue}"]`);
            console.log("lllllllllll",dimensionValue, dimElem)
            //if (!dimElem || dimElem.classList.contains('hidden')) return false;
            console.log("****** onload dimension is clicked automatically****")
            //dimElem.click();// commented on 17-12-25 e

            // added code 17-12-25 for pillow by default size select
           const noOfElement = _this.calculateNofoelements(selectedVariantVal.option1)
           console.log("noOfElement===",noOfElement);
           if(noOfElement == 1){
               dimElem.click();
               _this.dimension_border_set();
               $(".dropdown-arrow").hide();
               $(".dimension_wrapper").hide();
            }else{
              _this.updateStickyBarForATCVerifyButton(1); // added code on 20-12-25
            }
          }
        }

      }
      calculateNofoelements(option1){
          const noOfElement = document.querySelectorAll(`.dimension_val[data-option1="${option1}"]`).length;
          return noOfElement
      }

      getAlltheSelectedValues(){
        console.log("**************8getAlltheSelectedValues");
         const selectedVariant = document.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        console.log("selectedVarianttttttttttttttttttttt",selectedVariant);
        let selectedVariantVal = null;
        if (selectedVariant){
          try {
            selectedVariantVal = JSON.parse(selectedVariant);
          } catch (error) {
            console.error("Error parsing selectedVariant HTML:", error);
          }
        }

        console.log(selectedVariantVal.options);
        return selectedVariantVal.options;
      }

      getListOfselectedsize(value){
        console.log("getListOfselectedsize");
         this.onLoadFunction();
        if(value){
            var heightselected = document.querySelector(".product-form__input.height .height_option.active").getAttribute('data-value');
            console.log(heightselected);
        }
        var arraySelectedVariantArray = this.getAlltheSelectedValues();
        var dimensionPostion = this.findTheDimensionPostion(arraySelectedVariantArray);

        //document.querySelector(".custom-size").classList.remove('active');

        // const selectedCat = "Single";
        // if(!selectedCat.includes("x")){
        //    console.log("set value of cat on load 2");
        //    localStorage.setItem("selectedCat",selectedCat);
        // }

        // document.querySelectorAll('.dimension_val').forEach(el => {
        //   el.classList.add('hidden');
        // });  

        document.querySelectorAll('.dimension_val').forEach(el => {
                  el.classList.add('hidden');
                });
        var selectedSize =  localStorage.getItem("selectedCat");
        //var optionDimensionVal = this.optionDimensionValue(v.option2,v.option3);
        var availableColorVariantss;
        if(dimensionPostion == 3){
          availableColorVariantss = window.productData.variants
          /*.filter(v => v.option1 === selectedSize && v.available)*/
          .filter(v => v.option1 === arraySelectedVariantArray[0] && v.option2 === arraySelectedVariantArray[1])
          .map(v => this.optionDimensionValue(v.option2,v.option3));
          console.log("cccccnew",availableColorVariantss);
        }else{
          availableColorVariantss = window.productData.variants
          /*.filter(v => v.option1 === selectedSize && v.available)*/
          .filter(v => v.option1 === selectedSize)
          .map(v => this.optionDimensionValue(v.option2,v.option3));
          console.log("fvfvfvfvfv",availableColorVariantss);
        }
         availableColorVariantss.forEach(variantname=>{//third
            console.log(value);
            if(value){
             var dropDownList = document.querySelector(`.dimension_val[data-value="${variantname}"][data-sizevalue="${heightselected}"]`);
            }else{
              var dropDownList = document.querySelector(`.dimension_val[data-option1="${selectedSize}"][data-value="${variantname}"]`);
            }
            console.log("mohit",dropDownList,variantname);
            //console.log(`.dimension_val[data-value="${variantname}"][data-sizevalue="${heightselected}"]`);
            if(dropDownList){
              dropDownList.classList.remove('hidden');
            }
          });
          
      var selectedSize = localStorage.getItem("selectedSize");
      console.log("selectedSizeselectedSizeselectedSizeselectedSize",selectedSize);
      if(selectedSize == null){ 
          //console.log("selectedSizeselectedSizeselectedSizeselectedSize",selectedSize);
          const firstVisible = document.querySelector('li.dimension_val:not(.hidden)');
          if(firstVisible){
            var firstVisibleValue = firstVisible.getAttribute("data-value");
            firstVisible.click();
            console.log("setting size on load 2");
            localStorage.setItem("selectedSize",firstVisibleValue);
          }
      }

      }
      // Helper: Initialize default state (first height active, clear localStorage)
      initializeDefaults() {
        const firstHeightOpt = document.querySelector(".product-form__input.height .height_option");
        if(firstHeightOpt){
          firstHeightOpt.classList.add('active');
          document.querySelector(".custom-size-inputbox [name='custom-height']").value = firstHeightOpt.getAttribute("data-value");
          localStorage.setItem("selectedHeight", firstHeightOpt.getAttribute("data-value"));
        }

        localStorage.removeItem("customSize");
        localStorage.removeItem("selectedHeight");
        localStorage.removeItem("selectedSize");
      }

      // OPTIMIZED: Update sticky bar without AJAX, minimal DOM queries
      updateStickyText(){
       const stickyBar = document.querySelector(".product-main-sticky-bar"); 
       var defaultBuyButton = document.querySelector(".buy-buttons-block");
       const stickyBarVariant = stickyBar.querySelector(".product-sticky-variant");
       const spanElement = document.createElement("span");
       spanElement.textContent = 'Select a Size!';
       spanElement.style.color = 'red';
       defaultBuyButton.classList.remove('active');
       stickyBarVariant.replaceChildren(spanElement);

      }
      updateStickyBarForATCVerifyButton(value){
        //added code on 20-12-25 to set notify me , add to cart , select a size
        var _this = this;
        const stickyBarButton = document.querySelector(".sticky-addtocart-btn .btn-text");
        var textvalue = "";
        var class1 = "";
        var class2 = "";
        if(value == 1){

          var rmMetaText = document.querySelector(".rm-meta-text"); //for bed products Recommended mattress size:
          if(rmMetaText) rmMetaText.style.display = "none";// added code 20-12-25 on liquid file and here

          textvalue = "Select a Size";
          class1 = "available";
          class2 = "sold-out";
          _this.updateStickyText(); // select a size is show when click on buttons
        }else if(value == 2){
          textvalue = "Notify Me";
          class1 = "sold-out";
          class2 = "available";
        }else if(value == 3){
          textvalue = "Add to Cart";
          class1 = "available";
          class2 = "sold-out";
        }else{
          console.log("nothing happeded", value);
        }

        stickyBarButton.textContent = textvalue;
        stickyBarButton.classList.add(class1);
        stickyBarButton.classList.remove(class2);
      }

      updateStickyBarForVariant(variantId) {
        try {
          const productJson = document.querySelector('#ProductJson');
          if (!productJson) return;

          const product = JSON.parse(productJson.textContent);
          const variant = product.variants.find(v => v.id === parseInt(variantId));
          if (!variant) return;

          // Cache selectors (single query for sticky bar elements)
          const stickyBar = document.querySelector(".product-main-sticky-bar");
          if (!stickyBar) return;

          const stickyBarVariant = stickyBar.querySelector(".product-sticky-variant");
          const dropdownText = document.querySelector('.custom-dropdown-button-text');

          // Only update if dropdown has a size selected
          
          //if (dropdownText?.textContent.trim() === 'Select a Size') return; // commented on 17-12-25 it is not changed when select a size text is there
          // OPTIMIZED: Format options inline, no function calls

          //added code on 18-12-25 for stickybar issue
            const parts = [];
            const customOpt = document.querySelector(".product__info-wrapper .variant-pill-list.product-type");
            // console.log("customOpt >>> ",customOpt);
              if (customOpt) {
                const activeLink = customOpt.querySelector("a.active");
                if (activeLink) {
                  const labelText = activeLink.textContent.trim();
                  // console.log("Active Product-Type Label:", labelText);
                  if (labelText) parts.push(labelText);
                }
              }
            const checkedInput = customOpt?.querySelector("input[type='radio']:checked");
            // console.log("checkedinput",checkedInput);
            if (checkedInput) {
              console.log("checkedinput",checkedInput);
              const label = customOpt.querySelector(`label[for="${checkedInput.id}"]`);
              const labelText = label?.textContent.trim() || "";
              console.log("Checked Label:", labelText);
              if(labelText) parts.push(labelText);
            }

            const activeHeightElem = document.querySelector(".height_option.active");
            const heightVal = activeHeightElem ? activeHeightElem.getAttribute("data-value") : null;

            const formatOption = (option) => {
              if (!option) return option;
              const lower = option.toLowerCase();
              // Check for "x" for dimensions
              if (lower.includes('x')) {
                return `${option} inches`;
              }
              // Check for "pillow" and extract number
              if (lower.includes('pillow')) {
                const match = option.match(/^(\d+)\s*pillow/i);
                if (match) {
                  return `pack of ${match[1]}`;
                }
              }
              return option;
            };

            if (variant.option1) parts.push(formatOption(variant.option1));
            if (heightVal) parts.push(`${heightVal} inch`);
            if (variant.option2) parts.push(formatOption(variant.option2));
            if (variant.option3) parts.push(formatOption(variant.option3));

            const formattedText = parts.join(' | ');
          //added code on 18-12-25 for stickybar issue

          //code commented on 18-12-25
          // const parts = [variant.option1, variant.option2, variant.option3]
          //   .filter(Boolean)
          //   .map(opt => opt.toString()
          //     .split(/\s+/)
          //     .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
          //     .join(' ')
          //   );
          // console.log("dddddffffffffffff",parts);
          //code commented on 18-12-25
          if (stickyBarVariant) {
            //stickyBarVariant.textContent = parts.join(' | ');
            stickyBarVariant.textContent = formattedText;
         }

          // Update prices
          if (variant.price) {
            const priceElem = stickyBar.querySelector(".main-price");
            if (priceElem) {
              priceElem.textContent = Shopify.formatMoney(variant.price, Shopify.money_format).replace(/\.00$/, '');
            }
          }

          if (variant.compare_at_price) {
            const comparePriceElem = stickyBar.querySelector(".compare-price");
            if (comparePriceElem) {
              comparePriceElem.textContent = Shopify.formatMoney(variant.compare_at_price, Shopify.money_format).replace(/\.00$/, '');
            }
          }

        } catch (error) {
          console.error('[updateStickyBar]', error);
        }
      }

      // OPTIMIZED: Smart polling, batched updates, no AJAX during auto-select
      async autoSelectVariantFromUrl() {
        try {
          const urlParams = new URLSearchParams(window.location.search);
          const variantId = urlParams.get('variant');
          if (!variantId) return false;

          const productJson = document.querySelector('#ProductJson');
          if (!productJson) return false;

          const product = JSON.parse(productJson.textContent);
          const variant = product.variants.find(v => v.id === parseInt(variantId));
          if (!variant) return false;

          // Detect product structure: check which option contains dimension
          const dimensionPattern = /\d+\s*x\s*\d+/i;
          let mattressTypeValue, categoryValue, dimensionValue;

          if (dimensionPattern.test(variant.option2)) {
            // Structure: option1=category, option2=dimension (e.g., "King", "78 x 72 x 10")
            mattressTypeValue = null;
            categoryValue = variant.option1;
            dimensionValue = variant.option2;
          } else if (variant.option3 && dimensionPattern.test(variant.option3)) {
            // Structure: option1=type, option2=category, option3=dimension (e.g., "Hybrid Latex", "Queen", "78 x 60 x 8")
            mattressTypeValue = variant.option1;
            categoryValue = variant.option2;
            dimensionValue = variant.option3;
          } else {
            return false;
          }

          // Extract numeric dimension only (ignore labels like "Extra Large - ")
          const dimensionMatch = dimensionValue?.match(/(\d+\s*x\s*\d+(?:\s*x\s*\d+)?)/i);
          const numericDimension = dimensionMatch ? dimensionMatch[1].trim() : dimensionValue;

          // Extract height only if 3D (has 3 parts: length x width x height)
          let heightValue = null;
          if (numericDimension?.includes('x')) {
            const parts = numericDimension.split('x').map(p => p.trim());
            if (parts.length === 3) heightValue = parts[2];
          }

          // Suppress gallery updates and AJAX during auto-select
          this._autoSelecting = true;

          try {
            // Click mattress type radio (if product has 3 options)
            if (mattressTypeValue) {
              const mattressTypeRadio = document.querySelector(`input[type="radio"][value="${mattressTypeValue}"]`);
              if (mattressTypeRadio && !mattressTypeRadio.checked) mattressTypeRadio.click();
            }

            // Click category radio
            if (categoryValue) {
              const categoryRadio = document.querySelector(`input[type="radio"][value="${categoryValue}"]`);
              if (categoryRadio && !categoryRadio.checked) categoryRadio.click();
            }

            // Click height filter (if product has height)
            if (heightValue) {
              const heightElem = document.querySelector(`.height_option[data-value="${heightValue}"]`);
              if (!heightElem) return false;

              heightElem.dataset.trigger = "true";
              heightElem.click();

              // Smart poll for dimension visibility
              const dimensionReady = await this.waitForDimensionReady(dimensionValue, 100);
              if (!dimensionReady) return false;
            }

            // Click dimension
            if (dimensionValue) {
              const dimElem = document.querySelector(`.dimension_val[data-value="${dimensionValue}"]`);
              if (!dimElem || dimElem.classList.contains('hidden')) return false;
              dimElem.click();
            }

            // Re-enable events
            this._autoSelecting = false;

            // Batch final updates in single frame
            requestAnimationFrame(() => {
              const finalVariant = document.querySelector(".cst-product-slider-wrapper .pdp-main-slider-wrap")?.getAttribute("data-seleted-alt");
              if (finalVariant) this.cstUpdateMedia(finalVariant);
              this.updateStickyBarForVariant(variantId);
            });

            return true;

          } finally {
            this._autoSelecting = false;
          }

        } catch (error) {
          console.error('[Auto-Select]', error);
          this._autoSelecting = false;
          return false;
        }
      }

      // OPTIMIZED: Smart polling - wait only as long as needed
      waitForDimensionReady(dimensionValue, maxWait = 100) {
        return new Promise((resolve) => {
          const startTime = Date.now();
          const check = () => {
            const dimElem = document.querySelector(`.dimension_val[data-value="${dimensionValue}"]`);

            // Success: element exists and is visible
            if (dimElem && !dimElem.classList.contains('hidden')) {
              resolve(true);
              return;
            }

            // Timeout: give up if taking too long
            if (Date.now() - startTime >= maxWait) {
              resolve(false);
              return;
            }

            // Continue checking
            requestAnimationFrame(check);
          };
          check();
        });
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      formatINR(priceInCents) {
        const price = priceInCents / 100;
        return new Intl.NumberFormat('en-IN', { 
            style: 'currency', 
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(price);
     }
    //  updateValuesFunction(productData,variant){
    //     console.log("variantvariantvariantvariant",variant);
    //     const _this = this;
    //     var productUrl = "/products/"+productData.handle;
    //     var variantId  = variant.id;
    //     //console.log(productData,option1Value,option2Value,variantId);
    //     $(".product-variant-id").val(variantId);
    //     //updateURL(productUrl, variant?.id);
    //     window.history.replaceState({}, '', `${productUrl}${variantId ? `?variant=${variantId}` : ''}`);
    //     $(".product-form__buttons button").removeAttr("disabled");

    //     var firstVariPrice = _this.formatINR(variant.price);   
    //     var firstVari_C_Price = _this.formatINR(variant.compare_at_price); 
    //     var firstVari_C_Price_n = variant.compare_at_price;
    //     var sku = variant.sku; 

    //     console.log("sssssssssccccccccccccccc",firstVariPrice,firstVari_C_Price,firstVari_C_Price_n,variant);
    //     console.log("*********************dddd",sku,$("#pincode"));
    //     $(".compare_price").attr("data-compare-price",firstVari_C_Price_n);  
    //     $(".compare_price .price-item.price-item--regular").text(firstVari_C_Price);  
    //     $(".sticky-pricebox .compare-price").text(firstVari_C_Price);  
    //     $(".store-price .price-item.price-item--sale.price-item--last").text(firstVariPrice);  
    //     $(".sticky-pricebox .main-price").text(firstVariPrice);
    //     $("#pincode").attr("data-sku", sku);
    //   }



      findTheOptionSize(){ //9-12-25
        const selectedVariant = document.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        console.log("selectedVarianttttttttttttttttttttt",selectedVariant);
        let selectedVariantVal = null;
        if (selectedVariant){
          try {
            selectedVariantVal = JSON.parse(selectedVariant);
          } catch (error) {
            console.error("Error parsing selectedVariant HTML:", error);
          }
        }
        if(selectedVariantVal){
          if(selectedVariantVal.option2){
             if(selectedVariantVal.option3){
                console.log("hhhhhhhhhhhhhhhhhhhh");
                return 3;
             }else{
                console.log("hfffffhhhhhhhhhhhhhhhhhhh");
                return 2;
             }
          }  
        }
      }

      sizeElems(){
        const _this = this;
        var defaultBuyButton = document.querySelector(".buy-buttons-block");
        var stickyBar = document.querySelector(".product-main-sticky-bar");
         var stickyBarButton = stickyBar.querySelector(".sticky-addtocart-btn .btn-text")
        // START ---- Product page custom size dropdown
         var stickyBarVariant = stickyBar.querySelector(".product-sticky-variant");
        const quantityButtons = document.querySelectorAll(".product__info-wrapper .quantity__button");
        
        var productInfoElem = document.querySelector(".custom-dropdown-button-text");
        var customSizeDropdown = document.querySelector(".dimension-custom-dropdown");
        
        const customOp = document.querySelector(".custom-size");
        if(customOp && !(customOp.classList.contains("active"))){
          localStorage.removeItem("customSize");
        }

        if(customSizeDropdown){
          const textLabel = productInfoElem.textContent .trim().replace(/\s+/g, " ");
          // console.log("productInfoElem textContent",productInfoElem.textContent);
          if(textLabel == 'Select a Size'){
            defaultBuyButton.classList.remove('active');
            // stickyBarButton.textContent = 'Select Size';
            quantityButtons.forEach(qtyBtn=>{
              qtyBtn.classList.add('disabled');
              qtyBtn.setAttribute('disabled','disabled');
            });
          }else{
            const dd_hide = document.querySelector(".addOnProducts-wrapper.hidden");
            if (dd_hide){
              dd_hide.classList.remove("hidden");
            }
          }
                 
          var customSizeElems = customSizeDropdown.querySelectorAll(".dimension_wrapper .dimension_val");
         
          var filteredSizeElems = [...customSizeElems].filter(elem => 
              !elem.classList.contains("hidden") && !elem.hasAttribute("disabled")
          );
          filteredSizeElems.forEach((sizeElem) => {
            sizeElem.addEventListener('click', function() {
              /***************start added code on 12-12-25************ */
              const delpicMsg = document.querySelector(".delivery_input_main span#delpicMsg");
              if (delpicMsg) {
                delpicMsg.innerHTML = "";
              }
              /***************end added code on 12-12-25************ */
              //console.log("mohitthird click on dimension"); 
              $('.dimension_wrapper .dimension_val').removeClass('selected').removeAttr('selected');
              //$('.convert').removeClass('active');
              
              var selectedSize = this.getAttribute("data-value");
              
              //added code on 17-12-25 for cm feet click not set the "select size value"
              var customSizeDropdown = document.querySelector(".dimension-custom-dropdown");
              const productInfoElem = customSizeDropdown.querySelector(".custom-dropdown-button-text");
              productInfoElem.setAttribute("data-selected",selectedSize);
              //added code on 17-12-25 for cm feet click not set the "select size value"

              this.setAttribute("selected","selected");
              this.classList.add("selected");
               console.log("setting size on load 3");
              localStorage.setItem("selectedSize", selectedSize);

               _this.updatermMetaText(selectedSize); //added on 18-12-25 for updatermMetaText(option)
              // console.log("selectedSize >>> ",selectedSize);
              //document.querySelector(".product-form__input.dimension input[value='" + selectedSize + "']")?.click();

              /********************01-12-25*********************/
              
              const option1Value = localStorage.getItem("selectedCat");;
              
              const productData = window.productData;
              var findSize = _this.findTheOptionSize();
              console.log("findSize",findSize);
              var variant;
              if(findSize == 2){
                  const option2Value = selectedSize;
                  variant = window.productData.variants.find(v =>
                      v.option1 === option1Value &&
                      v.option2 === option2Value
                  );
                  console.log("ffff",variant);
              }else if(findSize == 3){
                  const option2Value = _this.getSizeColorValue(localStorage.getItem("selectedcolor"),selectedSize,"c") ;
                  const option3Value = _this.getSizeColorValue(localStorage.getItem("selectedcolor"),selectedSize,"s");
                  console.log(option1Value,option2Value,option3Value,selectedSize);
                  variant = window.productData.variants.find(v =>
                      v.option1 === option1Value && 
                      v.option2 === _this.getOption2Option3value(option2Value,option3Value,v.option2) &&
                      v.option3 === _this.getOption2Option3value(option2Value,option3Value,v.option3)
                  );
                  console.log("fffflllllllllllll",variant);
              }
              
              _this.updateValuesFunction(productData,variant);// update the price//used in original-mattress
              _this.renderAddOnWrapper(variant.id); 
              /********************01-12-25*********************/


             quantityButtons.forEach(qtyBtn=>{
                qtyBtn.classList.remove('disabled');
                qtyBtn.removeAttribute('disabled','disabled');
              });
              const existingError = document.querySelector(".size-selection-error");
              if (existingError) {
                  existingError.remove(); // Remove the error message completely
              }
              
              //const dd_hide = document.querySelector(".addOnProducts-wrapper.hidden");
              // console.log("remove hiddeeeeeeeeeen 2");
              // if (dd_hide){
              //   dd_hide.classList.remove("hidden"); //commented on 12-12 addon popup showing always
              // }
              
              
             productInfoElem.textContent = selectedSize + ' inches';
              defaultBuyButton.classList.add('active');
              const stickyBtnText = stickyBarButton.getAttribute("data-text");
              // stickyBarButton.textContent = 'Add To Cart';
              stickyBarButton.textContent = stickyBtnText; 
              stickyBarButton.classList.remove("sold-out");
              stickyBarButton.classList.add("available");

              //added code on 18-12-25 due to sticky notify is not showing
              console.log("variant.available",variant.available);
              if(variant.available == false){
                console.log("elseeeee");
                stickyBarButton.textContent = 'Notify Me';
                stickyBarButton.classList.add("sold-out");
                stickyBarButton.classList.remove("available");
              }
              //added code on 18-12-25 due to sticky notify is not showing
              var selectedHeightOption = document.querySelector(".height_option.active");
              if (selectedHeightOption) {
                localStorage.setItem("selectedHeight", selectedHeightOption.getAttribute("data-value"));
              }
             

              document.querySelector(".product-form__input.dimension-size input[value='" + selectedSize + "']")?.click();

              // ─────────────────────────────────────────────────────────────────────
              // ROBUST MTO UPDATE: Directly update MTO state after dimension selection
              // This ensures MTO is updated even if radio change event doesn't trigger AJAX
              // (e.g., when radio was already checked, or AJAX is slow/fails)
              // ─────────────────────────────────────────────────────────────────────
              setTimeout(() => {
                const selectedVariantInput = document.querySelector('product-form input[name="id"]');
                const selectedVariantId = selectedVariantInput?.value;
                if (selectedVariantId && window.MTOMessageManager) {
                  console.log('[Dimension Click] Direct MTO update for variant:', selectedVariantId);
                  window.MTOMessageManager.updateForVariant(selectedVariantId);
                }
              }, 100); // Small delay to ensure variant input is updated
              $('.convert_inch').click();;// added by 23-12-25
            });
          });
          
          customSizeDropdown.querySelector(".custom-dropdown-button")?.addEventListener('click',function(){
            this.classList.toggle('active');
          });
        }else{
          const dd_hide = document.querySelector(".addOnProducts-wrapper.hidden");
          if (dd_hide){
            dd_hide.classList.remove("hidden");
          }
        }


        var heightOptions = document.querySelectorAll(".product-form__input.height .height_option");
        if(heightOptions.length){
          heightOptions.forEach((heightOption)=>{
            //console.log("mohit",heightOptions);
            document.querySelectorAll('.dimension_val').forEach(el => {
              el.classList.add('hidden');
            });

            heightOption.addEventListener('click',function(){

              const dd_hide = document.querySelector(".addOnProducts-wrapper");
              if (dd_hide){
                dd_hide.classList.add("hidden");
              }
                 $('.convert_inch').click(); //added code on 18-12-25 to reset inches in dropdown

                //const heightVal = this.getAttribute("data-value");
                var heightVal = this.getAttribute("data-value");
                console.log(heightVal);
                //console.log("mohit Height Option Clicked...");
                heightOptions.forEach((heightOption)=>{
                  heightOption.classList.remove("active");
                });
                this.classList.add('active');
              

                // const avaible_dimension = document.querySelector('.variant-pill-list.dimension .variant-pill.quantity-available input');
                // if(avaible_dimension){
                //   console.log("avaible_dimension",avaible_dimension);
                //   avaible_dimension?.click();
                // }else{
                //   const available = document.querySelector('.variant-pill-list.dimension input:not(.disabled)');
                //   console.log("available",available); 
                //   available.click();
                // }
              // const customOp = document.querySelector(".custom-size");
              // console.log("customOp",customOp);

                localStorage.setItem("selectedHeight", heightVal);
                console.log(heightVal);
                const customHeightInput = document.querySelector(".custom-size-wrapper .height-option input");
                 //console.log("customHeightInput",customHeightInput);
                if(customHeightInput) customHeightInput.value = heightVal;

                // const customOp = document.querySelector(".custom-size");
                // console.log("customOp",customOp);

                console.log("cutsom size function called....");
                _this.customSize();// commented on 28-11-25 // uncommented on 01-12-25
             
              //remove rrormsg on heightOption selection
              var existingError = document.querySelector(".size-selection-error");
              if (existingError) {
                existingError.remove(); // Remove the error message completely
              }
              //delivery msg For size select
              const delpicMsg = document.querySelector(".delivery_input_main span#delpicMsg");
              if (delpicMsg) {
                delpicMsg.innerHTML = "";
              }
              
              hideDimension(heightVal,false);
              if (!this.dataset.trigger) {
                productInfoElem.removeAttribute("data-selected");
                // console.log("select a size sets");
                productInfoElem.textContent = "Select a Size"; // Reset size selection
                quantityButtons.forEach(qtyBtn=>{
                  qtyBtn.classList.add('disabled');
                  qtyBtn.setAttribute('disabled','disabled');
                });
                
                const dd_hide = document.querySelector(".addOnProducts-wrapper");
                if (dd_hide){
                  dd_hide.classList.add("hidden");
                }

                const isCustomSizeActive = document.querySelector(".variant-pill-list-wrap .custom-size.active");
                if(isCustomSizeActive){
                  // Custom size mode is active - keep showing custom MTO message
                  // MTO state stays as CUSTOM_SIZE_ACTIVE (no change needed)
                }else{
                 // set text to varinat sticky bar on pdp page
                  const spanElement = document.createElement("span");
                  spanElement.textContent = 'Select a Size!';
                  spanElement.style.color = 'red';

                  stickyBarVariant.replaceChildren(spanElement);
                  // set text to varinat sticky bar on pdp page
                  defaultBuyButton.classList.remove('active');
                  stickyBarButton.textContent = 'Select Size';

                  // ─────────────────────────────────────────────────────────────
                  // HOOK 3: Height change resets to "Select a Size"
                  // Hide MTO message when no variant is selected
                  // ─────────────────────────────────────────────────────────────
                  MTOMessageManager.reset();

                  // ─────────────────────────────────────────────────────────────
                  // BUG FIX: Don't remove selectedSize on height change
                  // This allows restoration when user returns to the same height
                  // (Scenario E: Height Change and Return)
                  // ─────────────────────────────────────────────────────────────
                  // localStorage.removeItem("selectedSize"); // COMMENTED OUT - Bug fix
                }
              }
              // Remove the data-trigger after execution so future clicks behave normally
              this.removeAttribute("data-trigger"); // Reset the attribute
              /****mohit 2-12-25 */
              document.querySelectorAll('.dimension_val').forEach(el => {
                  el.classList.add('hidden');
                });              
              var selectedSize =  localStorage.getItem("selectedCat");;
              var selectedcolor =  localStorage.getItem("selectedcolor");;

              var arraySelectedVariantArray = _this.getAlltheSelectedValues();
              var dimensionPostion = _this.findTheDimensionPostion(arraySelectedVariantArray);

              var availableColorVariants;
              if(dimensionPostion == 3){
                availableColorVariants = window.productData.variants
                /*.filter(v => v.option1 === selectedSize && v.available)*/
                .filter(v => v.option1 === selectedSize && v.option2 === selectedcolor)
                .map(v => _this.optionDimensionValue(v.option2,v.option3));
                console.log("cccccnew",availableColorVariants);
              }else{
                availableColorVariants = window.productData.variants
              /*.filter(v => v.option1 === selectedSize && v.available)*/
              .filter(v => v.option1 === selectedSize)
              .map(v => _this.optionDimensionValue(v.option2,v.option3));
              }
              console.log(availableColorVariants);
              console.log("availableColorVariants second");
              availableColorVariants.forEach(variantname=>{//second
                  const dropDownList = document.querySelector(`.dimension_val[data-value="${variantname}"][data-sizevalue="${heightVal}"]`);
                  console.log("=======",dropDownList,variantname);
                  //console.log(`.dimension_val[data-value="${variantname}"][data-sizevalue="${heightVal}"]`);
                  if(dropDownList){
                    dropDownList.classList.remove('hidden');
                    //dropDownList.classList.add('fffffff');
                  }
                });
            });
          });
        }else{
          console.log("if Noooo height 6 inch 8 inch option is there");
          document.querySelectorAll('.dimension_val').forEach(el => {
              el.classList.add('hidden');
            });
        }
        // const customOp = document.querySelector(".custom-size");
        //       console.log("customOp",customOp);
        console.log("customSize function called...");
          _this.customSize();
        var selectedHeightElem = document.querySelector(".height_option.active");
        // console.log("selectedHeightElem",selectedHeightElem);
        if(selectedHeightElem){
          var selectedHeightval = selectedHeightElem.getAttribute("data-value");
          hideDimension(selectedHeightval,true);
          _this.getListOfselectedsize(true);
        }else{
          _this.getListOfselectedsize(false);
          console.log("else getListOfselectedsize");
        }
        function hideDimension(heightVal,onpageLoad){
          filteredSizeElems.forEach((sizeElem) => {
            var size_value = sizeElem.getAttribute("data-value");
        
            if (size_value) {  
              let parts = size_value.split(" x ");
              let lastValue = parseInt(parts[parts.length - 1], 10);
              // console.log("lastValue", lastValue);
      
              if (heightVal == lastValue) {
                //console.log("mohitsizeElem");
                //console.log(sizeElem);
                //sizeElem.classList.remove('hidden');// commented on 2-12-25 ens
              } else {
                //sizeElem.classList.add('hidden');// commented on 2-12-25 ens
              }
            } else {
                console.log("Skipping element, no data-value attribute.");
            }
          });
          const visibleDimensionInputs = document.querySelectorAll(
              '.dimension .variant-pill:not(.hidden) input[type="radio"]'
            );
      
              // console.log("visibleDimensionInputs",visibleDimensionInputs);
              // 2. Extract unique height values from dimension inputs
              const heightSet = new Set();
            
              visibleDimensionInputs.forEach(input => {
                const value = input.value.trim(); // "78 x 36 x 10"
                const parts = value.split("x").map(p => p.trim());
                const height = parts[parts.length - 1]; // Get "10"
                if (height) {
                  heightSet.add(height);
                }
              });
              // console.log("heightSet",heightSet);
              var heightOptions = document.querySelectorAll(".product-form__input.height .height_option");
              heightOptions.forEach(option => {
                const value = option.getAttribute("data-value");
                if (heightSet.has(value)) {
                  option.closest(".variant-pill").classList.remove("hidden");
                  option.classList.remove("hidden");
                } else {
                  option.classList.add("hidden");
                  option.closest(".variant-pill").classList.add("hidden");
                }
              });
         

          if (selectedHeightElem && selectedHeightElem.classList.contains("hidden") && onpageLoad) {
            console.warn("Active height option is now hidden. Clicking first visible one...");
          
            const firstVisible = document.querySelector(".product-form__input.height .height_option:not(.hidden)");
            if (firstVisible) {
              firstVisible.dataset.trigger = "true";
              firstVisible.click();
            }
          }
        }
        
        // END ---- Product page custom size dropdown

         // product page heightOption Tooltip
        const heightTooltip = document.querySelector(".product-form__input.height .tooltip-title");
        if(heightTooltip){
          heightTooltip.addEventListener('click',function(){
            this.classList.toggle('active');
          });
        }
        document.addEventListener('click', function(event) {
          if (
            !event.target.closest('.tooltip-button') &&
            !event.target.closest('.tooltip-popup')
          ) {
            heightTooltip?.classList.remove('active'); // Replace with the actual class you want to remove
          }
        });
      }

      sizeConverter(){
        var _this = this;
         var customSizeDropdown = document.querySelector(".dimension-custom-dropdown");

        var converterButtons = document.querySelectorAll(".converter_wrapper .convert");
        
        if (converterButtons.length) {
          converterButtons.forEach((btn) => {
            btn.addEventListener("click", function (e) {
              e.preventDefault();
        
              // Remove active from all, add to clicked
              converterButtons.forEach((elemBtn) => elemBtn.classList.remove("active"));
              btn.classList.add("active");
        
              if (customSizeDropdown) {
                var customSizeElems = customSizeDropdown.querySelectorAll(".dimension_wrapper .dimension_val");
        
                var filteredSizeElems = [...customSizeElems].filter(elem =>
                  !elem.classList.contains("hidden") && !elem.hasAttribute("disabled")
                );
        
                var selectedDimension = customSizeDropdown.querySelector(".dimension_val[selected='selected']");
                var selectedValElement = customSizeDropdown.querySelector(".custom-dropdown-button-text");
                if (!selectedDimension) return;
        
                var selectedSizeVal = selectedValElement.getAttribute("data-selected");
        
                // Extract label and dimensions from selected
                let selectedLabel = "";
                let selectedRawVal = selectedSizeVal;
                if (selectedSizeVal && selectedSizeVal.includes("-")) {
                  const parts = selectedSizeVal.split("-");
                  selectedLabel = parts[0].trim();
                  selectedRawVal = parts.slice(1).join("-").trim();
                }
        
                let convertedVal = selectedSizeVal; // fallback
        
                filteredSizeElems.forEach((sizeElem) => {
                  var size_val = sizeElem.getAttribute("data-value");
                  if (!size_val) return;
        
                  // Extract label and raw dimension
                  let label = "";
                  let raw_val = size_val;
                  if (size_val.includes("-")) {
                    const parts = size_val.split("-");
                    label = parts[0].trim();
                    raw_val = parts.slice(1).join("-").trim();
                  }
        
                  let converted_val = raw_val;
                  let unit = "";
        
                  // Convert to CM
                  if (btn.classList.contains("convert_cm")) {
                    unit = "cm";
                    converted_val = raw_val
                      .split("x")
                      .map(v => (parseFloat(v.trim()) * 2.54).toFixed(1))
                      .join(" x ");
                    sizeElem.textContent = label ? `${label} - ${converted_val}` : `${converted_val}`;
                    if (raw_val === selectedRawVal) {
                      convertedVal = label ? `${label} - ${converted_val} ${unit}` : `${converted_val} ${unit}`;
                    }
                  }
        
                  // Convert to Feet
                  else if (btn.classList.contains("convert_feet")) {
                    unit = "feet";
                    converted_val = raw_val
                      .split("x")
                      .map(v => {
                        let feet = parseFloat(v.trim()) / 12;
                        return feet.toFixed(2).replace(/\.0+$/, '').replace(/(\.\d)0+$/, '$1');
                      })
                      .join(" x ");
                    sizeElem.textContent = label ? `${label} - ${converted_val}` : `${converted_val}`;
                    if (raw_val === selectedRawVal) {
                      convertedVal = label ? `${label} - ${converted_val} ${unit}` : `${converted_val} ${unit}`;
                    }
                  }
        
                  // Reset to Inches
                  else if (btn.classList.contains("convert_inch")) {
                    unit = "inches";
                    sizeElem.textContent = size_val;
                    if (size_val === selectedSizeVal) {
                      convertedVal = size_val + " " + unit;
                    }
                  }
                });
        
                // Set final selected display
                if (convertedVal) {
                  const labelval = selectedValElement.textContent;
                  console.log("labelval",labelval);
                    //if (labelval !== "Select a Size") { commented on 18-12-25 due to size value was not selected
                      selectedValElement.textContent = convertedVal;
                    //}
                    // selectedValElement.textContent = convertedVal;
                     _this.activateAddTocartButton();
                  
                  
                } else {
                  _this.deactivateAddTocartButton();
                  selectedValElement.textContent = 'Select a Size';
                  const dd_hide = document.querySelector(".addOnProducts-wrapper");
                  if (dd_hide) {
                    dd_hide.classList.add("hidden");
                  }
                }
        
              } else {
                // No dropdown fallback
                const sizeElem = document.querySelector(".dimension.noDropdown .dimension_val");
                if (sizeElem) {
                  let size_val = sizeElem.getAttribute("data-value");
                  if (size_val) {
                    let label = "";
                    let raw_val = size_val;
                    if (size_val.includes("-")) {
                      const parts = size_val.split("-");
                      label = parts[0].trim();
                      raw_val = parts.slice(1).join("-").trim();
                    }
        
                    let parts = raw_val.split("x").map(v => parseFloat(v.trim()));
                    let convertedVal = raw_val;
                    let unit = "";
        
                    if (btn.classList.contains("convert_cm")) {
                      unit = "cm";
                      convertedVal = parts.map(v => (v * 2.54).toFixed(1)).join(" x ");
                      sizeElem.textContent = label ? `${label} - ${convertedVal} ${unit}` : `${convertedVal} ${unit}`;
                    } else if (btn.classList.contains("convert_feet")) {
                      unit = "feet";
                      convertedVal = parts.map(v => {
                        let feet = v / 12;
                        return feet.toFixed(2).replace(/\.0+$/, '').replace(/(\.\d)0+$/, '$1');
                      }).join(" x ");
                      sizeElem.textContent = label ? `${label} - ${convertedVal} ${unit}` : `${convertedVal} ${unit}`;
                    } else if (btn.classList.contains("convert_inch")) {
                      unit = "inches";
                      sizeElem.textContent = size_val + " " + unit;
                    }
                  }
                }
              }
            });
          });
        }

        // END --- Product page inch to cm,feet converter
      }

      customSize(){
        const existingInput = document.querySelector("#custom_size");
        if (existingInput) {
          existingInput.remove(); // Remove it if found
        }
        //execute if customSize active 
        function isCustomSize(){

          function restrictToNumericInput(inputElement) {
            inputElement.addEventListener("keydown", function (e) {
              const allowedKeys = [
                "Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab",
              ];
          
              // Allow number keys (0–9) and allowed control keys
              if (
                (e.key >= "0" && e.key <= "9") ||
                allowedKeys.includes(e.key)
              ) {
                return; // Allow input
              } else {
                e.preventDefault(); // Block anything else (e.g., letters, symbols, etc.)
              }
            });
          }
          const defaultBuyButton = document.querySelector(".buy-buttons-block");
          const isCustomActive = document.querySelector(".custom-size.active");
          const pincodeInput = document.querySelector(".pincode-wrapper input[id='pincode']");
          const customWrapper = document.querySelector(".custom-size-wrapper");
          const customBuyButton = document.querySelector(".product__info-wrapper .custom_buy_button");

         
          console.log("isCustomActive",isCustomActive);

          const isCustomsize = localStorage.getItem("customSize");
          console.log("isCustomsize",isCustomsize);
          
          
          var dimensionOptions = document.querySelector(".product_customDropdown");
          if(isCustomActive && isCustomsize != null){
            console.log("cutsom opt activeted.......");
            if(defaultBuyButton) defaultBuyButton.classList.remove("active");
            if(pincodeInput) pincodeInput.setAttribute("data-custom",true);
            customWrapper.classList.remove("hidden");
            const inputs = document.querySelectorAll('.variant-pill-list.category input[type="radio"]');
            inputs.forEach(input => {
              input.checked = false;
            });
            //console.log("inputs",inputs);
            const cstbtntext = document.querySelector(".sticky-addtocart-btn .btn-text");
            // console.log("cstbtntext >>> ",cstbtntext);
            if(cstbtntext){
              cstbtntext.textContent = "Add to Cart"
            }
            if(dimensionOptions){
              dimensionOptions.classList.add('hidden');
            }

            var lengthInput = document.querySelector("input[name='custom-length']");
            var widthInput = document.querySelector("input[name='custom-width']");
            var heightInput = document.querySelector("input[name='custom-height']");
            console.log("widthInput-----", lengthInput, heightInput);
            if(heightInput){
              const heightValue = heightInput.value;
            }
            if (lengthInput) restrictToNumericInput(lengthInput);
            if (widthInput) restrictToNumericInput(widthInput);
            if (heightInput) restrictToNumericInput(heightInput); // Only if enabled later
            
            // const MIN_LENGTH = 60;
            // const MAX_LENGTH = 84;
            // const MIN_WIDTH = 24;
            // const MAX_WIDTH = 78;

            const MIN_LENGTH = Number(lengthInput.getAttribute('min')) || 60;
            const MAX_LENGTH = Number(lengthInput.getAttribute('max')) || 84;
            const MIN_WIDTH = Number(widthInput.getAttribute('min')) || 24;
            const MAX_WIDTH = Number(widthInput.getAttribute('max')) || 78;
            
            const showError = (message) => {
              console.log("message",message);
              const errorWrapper = document.querySelector(".error-msg-wrapper");
              const atcBtns = document.querySelectorAll(".atc-button");
              const stickyBar = document.querySelector(".product-main-sticky-bar");
              const cst_priceElem = document.querySelector(".product__info-wrapper .price");
              console.log("cst_priceElem",cst_priceElem);
              if (errorWrapper) {
                atcBtns.forEach((atcBtn)=>{
                  atcBtn.classList.add('hidden');
                });
                if(cst_priceElem){
                  cst_priceElem.classList.add('hidden');
                }
                stickyBar.classList.add("hidden");
                errorWrapper.textContent = message;
              }
            };

       
            // const handleCustomChange = () => {
            //   const lengthValue = parseInt(lengthInput.value.trim(), 10);
            //   const widthValue = parseInt(widthInput.value.trim(), 10);
            //   const heightValue = heightInput?.value.trim();
            //   const errorWrapper = document.querySelector(".error-msg-wrapper");
            //   const atcBtns = document.querySelectorAll(".atc-button");
            //   const stickyBar = document.querySelector(".product-main-sticky-bar");
            //   const cst_priceElem = document.querySelector(".product__info-wrapper .price");
            
            //   if (isNaN(lengthValue) || lengthValue < MIN_LENGTH || lengthValue > MAX_LENGTH) {
            //     showError(`Length customization is allowed between ${MIN_LENGTH} - ${MAX_LENGTH} inch`);
            //     return;
            //   }
            
            //   if (isNaN(widthValue) || widthValue < MIN_WIDTH || widthValue > MAX_WIDTH) {
            //     showError(`Width customization is allowed between ${MIN_WIDTH} - ${MAX_WIDTH} inch`);
            //     return;
            //   }
            
            //   if (errorWrapper) errorWrapper.textContent = '';
            //   atcBtns.forEach((atcBtn) => atcBtn.classList.remove('hidden'));
            //   if (cst_priceElem) cst_priceElem.classList.remove('hidden');
            //   stickyBar.classList.remove('hidden');
            
            //   const customDimensionVal = `${lengthValue} x ${widthValue} x ${heightValue}`;
            //   const matchedDimensions = [];
            //   const allDimensionElems = document.querySelectorAll(".dimension_wrapper .dimension_val");
            
            //   allDimensionElems.forEach((dimensionElem) => {
            //     const dimensionval = dimensionElem.getAttribute("data-value");
            //     const parts = dimensionval.split(/x|X/).map(p => p.trim());
            //     if (parts.length === 3 && parts[2] === heightValue) {
            //       matchedDimensions.push(dimensionval);
            //     }
            //   });
            //   console.log("matchedDimensions",matchedDimensions);
            
            //   const [customL, customW, customH] = customDimensionVal.split(/x|X/).map(p => parseInt(p.trim(), 10));
            //   const allAvailableDimensions = matchedDimensions.map(dim => {
            //     const [l, w, h] = dim.split(/x|X/).map(p => parseInt(p.trim(), 10));
            //     return { length: l, width: w, height: h, raw: dim };
            //   });
            
            //   const sameHeightDims = allAvailableDimensions.filter(dim => dim.height === customH);
            //   let possibleLengths = sameHeightDims.map(dim => dim.length).filter(l => l >= customL).sort((a, b) => a - b);
            //   let possibleWidths = sameHeightDims.map(dim => dim.width).filter(w => w >= customW).sort((a, b) => a - b);
            
            //   let matchedDimObj = null;
            //   outerLoop:
            //   for (let l of possibleLengths) {
            //     for (let w of possibleWidths) {
            //       for (let dim of sameHeightDims) {
            //         if (dim.length === l && dim.width === w) {
            //           matchedDimObj = dim;
            //           break outerLoop;
            //         }
            //       }
            //     }
            //   }
            
            //   if (!matchedDimObj && sameHeightDims.length > 0) {
            //     matchedDimObj = sameHeightDims.reduce((closest, dim) => {
            //       const diff = Math.abs(dim.length - customL) + Math.abs(dim.width - customW);
            //       const closestDiff = Math.abs(closest.length - customL) + Math.abs(closest.width - customW);
            //       return diff < closestDiff ? dim : closest;
            //     }, sameHeightDims[0]);
            //   }
            
            //   const product_sticky_variant = document.querySelector(".product-sticky-variant");
            //   const productForm = document.querySelector("product-form form");
            
            //   if (matchedDimObj) {
            //     const isExactMatch =
            //       matchedDimObj.length === customL &&
            //       matchedDimObj.width === customW &&
            //       matchedDimObj.height === customH;
            
            //     const finalDimension = `${matchedDimObj.length} x ${matchedDimObj.width} x ${matchedDimObj.height}`;
            
            //     const productDataEl = document.getElementById('ProductJson');
            //     let product = null;
            
            //     if (productDataEl) {
            //       try {
            //         product = JSON.parse(productDataEl.textContent);
            //       } catch (e) {
            //         console.error("Failed to parse product JSON:", e);
            //         return;
            //       }
            //     }
            
            //     const dimensionIndex = product?.options?.findIndex(opt => opt.toLowerCase() === "dimension");
            //     if (dimensionIndex === -1 || !product?.variants) {
            //       console.warn("No 'Dimension' option found or variants missing.");
            //       return;
            //     }
            
            //     const getVariantByDimension = (dimString) => {
            //       return product.variants.find(v => v.options[dimensionIndex] === dimString);
            //     };
            
            //     let matchedVariant = getVariantByDimension(finalDimension);
            
            //     // Remove existing input
            //     const existingInput = document.querySelector("#custom_size");
            //     if (existingInput) existingInput.remove();
            
            //     // Create and append hidden input
            //     const hiddenInput = document.createElement("input");
            //     hiddenInput.type = "hidden";
            //     hiddenInput.id = "custom_size";
            //     hiddenInput.name = "properties[custom_size]";
            //     hiddenInput.value = `${customDimensionVal} inches`;
            //     if (productForm) productForm.appendChild(hiddenInput);
            
            //     // Set ATC data
            //     customBuyButton.setAttribute("data-size", customDimensionVal + ' inches');
            //     customBuyButton.setAttribute("data-variant-id", matchedVariant?.id || "");
            
            //     if (isExactMatch) {
            //       if (product_sticky_variant) {
            //         product_sticky_variant.textContent = `${customDimensionVal} inches`;
            //       }
            
            //       if (matchedVariant && matchedVariant.available) {
            //         updateVariantDetails(matchedVariant);
            //       } else {
            //         showError("Oops! Sorry, the selected size is not available currently.");
            //       }
            //       return;
            //     }
            
            //     // Not exact match but valid matchedDimObj
            //     if (matchedVariant && matchedVariant.available) {
            //       if (product_sticky_variant) {
            //         product_sticky_variant.textContent = `Custom | ${customDimensionVal} inches`;
            //       }
            //       updateVariantDetails(matchedVariant);
            //     } else {
            //       // Try closest available dimensions (greater or equal)
            //       const greaterOrEqualDims = sameHeightDims
            //         .filter(dim => dim.length >= customL && dim.width >= customW)
            //         .sort((a, b) => {
            //           const diffA = (a.length - customL) + (a.width - customW);
            //           const diffB = (b.length - customL) + (b.width - customW);
            //           return diffA - diffB;
            //         });
            
            //       const availableGreaterDims = greaterOrEqualDims.filter(dim => {
            //         const variant = getVariantByDimension(`${dim.length} x ${dim.width} x ${dim.height}`);
            //         return variant && variant.available;
            //       });
            
            //       if (availableGreaterDims.length > 0) {
            //         const altDim = availableGreaterDims[0];
            //         const altFinalDimension = `${altDim.length} x ${altDim.width} x ${altDim.height}`;
            //         matchedVariant = getVariantByDimension(altFinalDimension);
            
            //         if (product_sticky_variant) {
            //           product_sticky_variant.textContent = `Closest available match | ${altFinalDimension} inches`;
            //         }
            
            //         customBuyButton.setAttribute("data-variant-id", matchedVariant.id);
            //         updateVariantDetails(matchedVariant);
            //       } else {
            //         console.warn("All closest greater dimensions are sold out.");
            //         showError("Sorry, no available sizes match are in stock.");
            //       }
            //     }
            //   }
            // };
          const handleCustomChange = () => {
         
              const lengthValue = parseInt(lengthInput.value.trim(), 10);
              const widthValue = parseInt(widthInput.value.trim(), 10);
           const form = document.querySelector('.product-form');
            const productId = form.querySelector('input[name="product-id"]').value;
            console.log("productId", productId);
            let heightValue;
            if(productId == 9105510793474)
            {
              heightValue = 9
            }
            else{
            heightValue = heightInput?.value?.trim(); // may be undefined or blank
            }
         
            console.log("heightValue", heightValue)
              const errorWrapper = document.querySelector(".error-msg-wrapper");
              const atcBtns = document.querySelectorAll(".atc-button");
              const stickyBar = document.querySelector(".product-main-sticky-bar");
              const cst_priceElem = document.querySelector(".product__info-wrapper .price");
            
              if (isNaN(lengthValue) || lengthValue < MIN_LENGTH || lengthValue > MAX_LENGTH) {
                showError(`Length customization is allowed between ${MIN_LENGTH} - ${MAX_LENGTH} inch`);
                return;
              }
            
              if (isNaN(widthValue) || widthValue < MIN_WIDTH || widthValue > MAX_WIDTH) {
                showError(`Width customization is allowed between ${MIN_WIDTH} - ${MAX_WIDTH} inch`);
                return;
              }
            
              if (errorWrapper) errorWrapper.textContent = '';
              atcBtns.forEach(btn => btn.classList.remove('hidden'));
              if (cst_priceElem) cst_priceElem.classList.remove('hidden');
              if (stickyBar) stickyBar.classList.remove('hidden');
            
              const customDimensionVal = heightValue ? `${lengthValue} x ${widthValue} x ${heightValue}` : `${lengthValue} x ${widthValue}`;
              const matchedDimensions = [];
              // const allDimensionElems = document.querySelectorAll(".dimension_wrapper .dimension_val");
              const allDimensionElems = document.querySelectorAll(".variant-pill-list.dimension input");
              //console.log("allDimensionElems", allDimensionElems)
              allDimensionElems.forEach((el) => {
                // const dim = el.getAttribute("data-value").trim();
                const dim = el.value.trim();
                const parts = dim.split(/x|X/).map(p => p.trim());
              //console.log("parts", parts)
                // Support both 2-part and 3-part dimensions
                if (parts.length === 2 || parts.length === 1) {
                  matchedDimensions.push(dim); // no height — accept directly
                } else if (parts.length === 3) {
                  const [ , , h ] = parts;
                  // Only add if height matches the input height
                  if (h == heightValue) {
                    matchedDimensions.push(dim);
                  }
                }
               
              });
              
              //console.log("matchedDimensions",matchedDimensions);
              const [customL, customW, customH] = [lengthValue, widthValue, heightValue ? parseInt(heightValue, 10) : null];
            
              const allAvailableDimensions = matchedDimensions.map(dim => {
                const parts = dim.split(/x|X/).map(p => parseInt(p.trim(), 10));
                return {
                  length: parts[0],
                  width: parts[1],
                  height: parts[2] || null,
                  raw: dim
                };
              });
            console.log("allAvailableDimensions", allAvailableDimensions)
              const sameHeightDims = allAvailableDimensions.filter(dim => {
                return heightValue ? dim.height === customH : true;
              });
            
              let possibleLengths = sameHeightDims.map(d => d.length).filter(l => l >= customL).sort((a, b) => a - b);
              let possibleWidths = sameHeightDims.map(d => d.width).filter(w => w >= customW).sort((a, b) => a - b);
            
              let matchedDimObj = null;
              outerLoop:
              for (let l of possibleLengths) {
                for (let w of possibleWidths) {
                  for (let dim of sameHeightDims) {
                    if (dim.length === l && dim.width === w) {
                      matchedDimObj = dim;
                      break outerLoop;
                    }
                  }
                }
              }
            
              if (!matchedDimObj && sameHeightDims.length > 0) {
                matchedDimObj = sameHeightDims.reduce((closest, dim) => {
                  const diff = Math.abs(dim.length - customL) + Math.abs(dim.width - customW);
                  const closestDiff = Math.abs(closest.length - customL) + Math.abs(closest.width - customW);
                  return diff < closestDiff ? dim : closest;
                }, sameHeightDims[0]);
              }
            
              const product_sticky_variant = document.querySelector(".product-sticky-variant");
              const productForm = document.querySelector("product-form form");
            console.log("matchedDimObj", matchedDimObj)
              if (matchedDimObj) {
                const isExactMatch =
                  matchedDimObj.length === customL &&
                  matchedDimObj.width === customW &&
                  (heightValue ? matchedDimObj.height === customH : true);
            
                const finalDimension = matchedDimObj.height != null
                  ? `${matchedDimObj.length} x ${matchedDimObj.width} x ${matchedDimObj.height}`
                  : `${matchedDimObj.length} x ${matchedDimObj.width}`;
            
                const productDataEl = document.getElementById('ProductJson');
                let product = null;
            
                if (productDataEl) {
                  try {
                    product = JSON.parse(productDataEl.textContent);
                  } catch (e) {
                    console.error("Failed to parse product JSON:", e);
                    return;
                  }
                }
            
                const dimensionIndex = product?.options?.findIndex(opt => opt.toLowerCase() === "dimension");
                if (dimensionIndex === -1 || !product?.variants) {
                  console.warn("No 'Dimension' option found or variants missing.");
                  return;
                }
            
                const getVariantByDimension = (dimString) => {
                  return product.variants.find(v => v.options[dimensionIndex] === dimString);
                };
            
                let matchedVariant = getVariantByDimension(finalDimension);
            
                const existingInput = document.querySelector("#custom_size");
                if (existingInput) existingInput.remove();
            
                // const hiddenInput = document.createElement("input");
                // hiddenInput.type = "hidden";
                // hiddenInput.id = "custom_size";
                // hiddenInput.name = "properties[custom_size]";
                // hiddenInput.value = `${customDimensionVal} inches`;
                // if (productForm) productForm.appendChild(hiddenInput);
            console.log("customDimensionVal", customDimensionVal);
            const customBuyButton1 = document.querySelector(".custom_buy_button");
            if(customBuyButton1){
              console.log("found!!!!!!!!")
            }
                customBuyButton.setAttribute("data-size", customDimensionVal + ' inches');
                customBuyButton.setAttribute("data-variant-id", matchedVariant?.id || "");
            
                if (isExactMatch) {
                  if (product_sticky_variant) {
                    product_sticky_variant.textContent = `${customDimensionVal} inches`;
                  }
            
                  if (matchedVariant && matchedVariant.available) {
                    updateVariantDetails(matchedVariant,isExactMatch);
                  } else {
                    showError("Oops! Sorry, the selected size is not available currently.");
                  }
                  return;
                }
            
                // Fallback to closest larger match
                if (matchedVariant && matchedVariant.available) {
                  if (product_sticky_variant) {
                    product_sticky_variant.textContent = `Custom | ${customDimensionVal} inches`;
                  }
                  updateVariantDetails(matchedVariant,isExactMatch);
                } else {
                  const greaterOrEqualDims = sameHeightDims
                    .filter(dim => dim.length >= customL && dim.width >= customW)
                    .sort((a, b) => {
                      const diffA = (a.length - customL) + (a.width - customW);
                      const diffB = (b.length - customL) + (b.width - customW);
                      return diffA - diffB;
                    });
            
                  const availableGreaterDims = greaterOrEqualDims.filter(dim => {
                    const dstr = dim.height != null
                      ? `${dim.length} x ${dim.width} x ${dim.height}`
                      : `${dim.length} x ${dim.width}`;
                    const variant = getVariantByDimension(dstr);
                    return variant && variant.available;
                  });
            
                  if (availableGreaterDims.length > 0) {
                    const altDim = availableGreaterDims[0];
                    const altFinalDimension = altDim.height != null
                      ? `${altDim.length} x ${altDim.width} x ${altDim.height}`
                      : `${altDim.length} x ${altDim.width}`;
                    matchedVariant = getVariantByDimension(altFinalDimension);
            
                    if (product_sticky_variant) {
                      product_sticky_variant.textContent = `Closest available match | ${altFinalDimension} inches`;
                    }
            
                    customBuyButton.setAttribute("data-variant-id", matchedVariant.id);
                    updateVariantDetails(matchedVariant,isExactMatch);
                  } else {
                    console.warn("All closest greater dimensions are sold out.");
                    showError("Sorry, no available sizes match are in stock.");
                  }
                }
              }
            };
            
            // ✅ Helper function to update variant price, ID, and discount
            function updateVariantDetails(matchedVariant,isExactMatch) {
              const product_form_input = document.querySelector("product-form input[name='id']");
              product_form_input.value = matchedVariant.id;
              customBuyButton.setAttribute("data-variant-id",matchedVariant.id);
              customBuyButton.setAttribute("isExactMatch",isExactMatch);
            
              const orig_price = document.querySelector(".price__sale .price-item--sale");
              const comp_price = document.querySelector(".price__sale .price-item--regular");
              const sticky_orig_price = document.querySelector(".product-main-sticky-bar .main-price");
              const sticky_comp_price = document.querySelector(".product-main-sticky-bar .compare-price");
              const discountPercentageEl = document.querySelector(".prestige-count");
              const mrp_wrapper_price = document.querySelector(".mrp_price_wrapper .compare_price .price-item--regular");
            
              const matche_orig_price = matchedVariant.price;
              const matche_comp_price = matchedVariant.compare_at_price;
            
              if (orig_price) {
                orig_price.textContent = `${Shopify.formatMoney(matche_orig_price, Shopify.money_format).replace(/\.00$/, "")}`;
              }
            
              if (sticky_orig_price) {
                sticky_orig_price.textContent = `${Shopify.formatMoney(matche_orig_price, Shopify.money_format).replace(/\.00$/, "")}`;
              }
            
              if (comp_price) {
                if (matche_comp_price && matche_comp_price > matche_orig_price) {
                  comp_price.textContent = `${Shopify.formatMoney(matche_comp_price, Shopify.money_format).replace(/\.00$/, "")}`;
                  comp_price.style.display = "inline";
                } else {
                  comp_price.style.display = "none";
                }
              }
            
              if (sticky_comp_price) {
                if (matche_comp_price && matche_comp_price > matche_orig_price) {
                  sticky_comp_price.textContent = `${Shopify.formatMoney(matche_comp_price, Shopify.money_format).replace(/\.00$/, "")}`;
                  sticky_comp_price.style.display = "inline";
                } else {
                  sticky_comp_price.style.display = "none";
                }
              }
            
              if (discountPercentageEl) {
                if (matche_comp_price && matche_comp_price > matche_orig_price) {
                  let discount = ((matche_comp_price - matche_orig_price) * 100 / matche_comp_price).toFixed(0);
                  discountPercentageEl.textContent = `(${discount}% OFF)`;
                } else {
                  discountPercentageEl.textContent = "";
                }
              }
              if (mrp_wrapper_price) {
                if (matche_comp_price && matche_comp_price > matche_orig_price) {
                  mrp_wrapper_price.textContent = `${Shopify.formatMoney(matche_comp_price, Shopify.money_format).replace(/\.00$/, "")}`;
                  mrp_wrapper_price.style.display = "inline";
                } else {
                  mrp_wrapper_price.style.display = "none";
                }
              }
              const pincodeInput = document.querySelector(".pincode-wrapper input#pincode");
  if (pincodeInput) {
    pincodeInput.setAttribute("data-sku", matchedVariant.sku);

    // Match the logic from line 1935-1943
    if(matchedVariant.available){
      pincodeInput.setAttribute("data-outofstock", true);
    } else {
      pincodeInput.setAttribute("data-outofstock", false);
    }
  }

  // Clear previous delivery message
  const delpicMsg = document.querySelector(".delivery_input_main span#delpicMsg");
  if (delpicMsg) {
    delpicMsg.innerHTML = "";
  }


            }

          
            // Bind change events
            lengthInput.addEventListener("input", handleCustomChange);
            widthInput.addEventListener("input", handleCustomChange);
            handleCustomChange();
            
            // allDimensionElems.forEach((dimensionElem)=>{
            //   const dimensionVal = dimensionElem.getAttribute("data-value");
            //   if(dimensionVal == finalDimension){
            //     console.log("dimensionElem",dimensionElem);
            //     dimensionElem.click();
            //   }
            // });
            var productForm = document.querySelector(".product-form form");
            if (productForm) {
              // Remove _custom_size input if it exists
              const existingCustomSizeInput = productForm.querySelector('input[name="properties[_custom_size]"]');
              if (existingCustomSizeInput) {
                productForm.removeChild(existingCustomSizeInput);
              }
      
              // Remove _is_mto input if it exists
              const existingIsMtoInput = productForm.querySelector('input[name="properties[_is_mto]"]');
              if (existingIsMtoInput) {
                productForm.removeChild(existingIsMtoInput);
              }
            }
          }else{
            // if(pincodeInput) pincodeInput.setAttribute("data-custom",false);
             if(customWrapper) customWrapper.classList.add('hidden');
            if(dimensionOptions) dimensionOptions.classList.remove('hidden');
           
            // if(defaultBuyButton) defaultBuyButton.classList.add("active");
          }
        }
        
        const customSizeOpt = document.querySelector(".custom-size");
        if(customSizeOpt){
          customSizeOpt.addEventListener("click",function(){
            const dd_hide = document.querySelector(".addOnProducts-wrapper");
            if (dd_hide){
              dd_hide.classList.add("hidden");
            }
            const quantityButtons = document.querySelectorAll(".quantity__button");
            quantityButtons.forEach(qtyBtn=>{
              qtyBtn.classList.remove('disabled');
              qtyBtn.removeAttribute('disabled','disabled');
            });
            this.classList.add("active");

            localStorage.setItem("customSize",'active');
            isCustomSize();

            // ─────────────────────────────────────────────────────────────────────
            // HOOK 6: Custom size mode entered
            // Show custom MTO message when user clicks "Custom Size"
            // ─────────────────────────────────────────────────────────────────────
            MTOMessageManager.setCustomSizeActive();
          });
      
            const checkisCustom = localStorage.getItem("customSize");
            if(checkisCustom){
              console.log("again click....");
              customSizeOpt.click();
            }else{
              customSizeOpt.classList.remove('active');
              localStorage.removeItem("customSize");

              // Select the wrapper element
              const wrapper = document.querySelector(
                ".product-form__input.product-form__input--pill.variant-pill-list.category"
              );

              // Get all inputs inside that wrapper
              const inputs = wrapper ? wrapper.querySelectorAll("input") : [];

              // Check if any input is checked
              const anyChecked = Array.from(inputs).some(input => input.checked);

              console.log("Any checked?", anyChecked);

                const sizedropdownText = document.querySelector(".custom-dropdown-button-text").textContent.trim();

                if (!(sizedropdownText === "Select a Size")) {
                 const themeBtn = document.querySelector(".buy-buttons-block.atc-button");
                 themeBtn.classList.add("active");
                } 

            }
        
          
        }
        // const isCustomsize = localStorage.getItem("customSize");
        // console.log("isCustomsize",isCustomsize);
        // // if(isCustomsize){
        // //   console.log("custom Opt active---------------");
        // // }

        isCustomSize();
      }
      
      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      // handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
      //   // Skip AJAX requests during auto-select to prevent background loading
      //   console.log("mohit handleOptionValueChange",selectedOptionValues);
      //   console.log(this._autoSelecting);
      //   if (this._autoSelecting) return;
      //   document.body.classList.add("variant-loader");
      //   if (!this.contains(event.target)) return;

      //   this.resetProductFormState();

      //   const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
      //   this.pendingRequestUrl = productUrl;
      //   const shouldSwapProduct = this.dataset.url !== productUrl;
      //   console.log("mohitshouldSwapProduct",shouldSwapProduct);
      //   const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

      //   this.renderProductInfo({
      //     requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
      //     targetId: target.id,
      //     callback: shouldSwapProduct
      //       ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
      //       : this.handleSwapProduct(productUrl, shouldFetchFullPage),
      //   });
      // }
      handleOptionValueChange({ data: { event, target, selectedOptionValues,selectedOptionValuesName } }) {
        if (!this.contains(event.target)) return;
        console.log("selectedOptionValues",selectedOptionValues,selectedOptionValuesName);
        this.resetProductFormState();
        var selectedOptionValuesLength = selectedOptionValues.length;
        var selectVariantCategory = target.getAttribute("name");
        //console.log("mohit",event.target.value,selectedOptionValues,target,event,selectedOptionValuesName);
        
        //var clickedelement = 0;
        var clickedelement = target.getAttribute("data-option-position");
        var isColor = false;
        var isCategory = false;
        if(/color/.test(selectVariantCategory)){
          console.log("third element clicked");
          isColor = true;
        }

        if(/category/.test(selectVariantCategory)){
          console.log("third element clicked");
          isCategory = true;
        }
        /**********15-12-25************/
        // if (/category/.test(selectVariantCategory)) {
        //   console.log("first element clicked");
        //   clickedelement = 1;
        // }else if(/dimension/.test(selectVariantCategory)){
        //   console.log("second element clicked");
        //   clickedelement = 2;
        // }else if(/color/.test(selectVariantCategory)){
        //   console.log("third element clicked");
        //   clickedelement = 3;
        // }else{
        //   console.log("unlisted element clicked");
        // }
        console.log(selectedOptionValuesLength,selectVariantCategory,clickedelement);
       const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl,selectedOptionValuesLength,clickedelement,selectedOptionValuesName,isColor,isCategory),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      updatePacksizePrice(){
        var _this = this;
        console.log("updatePacksizePrice");
        var selectedSize =  localStorage.getItem("selectedCat");
        const availableColorVariantss = window.productData.variants
        .filter(v => v.option1 === selectedSize)
        /*.map(v => this.optionDimensionValue(v.option2,v.option3)*/
        .map(v => ({
                    option2: this.optionDimensionValue(v.option2,v.option3),
                    price: v.price/100,
                    compare_at_price: this.formatINR(v.compare_at_price),
                    compareatprice: v.compare_at_price
                })      
        );
        console.log("bvbvvvvvvvvvvvvv",availableColorVariantss);
        
        // availableColorVariantss.forEach(variantname=>{//third
        //     var divideInPrice = parseInt($(".variant-pill-list.pack-size .variant-pill-list-wrap .packSizeClass").attr("data-divided-by"));
        //     console.log("divideInPrice",divideInPrice);
        //     var contentvalue = "Rs "+ variantname.price/divideInPrice +"/ pillow";
        //     $(".variant-pill-list.pack-size .variant-pill-list-wrap .packSizeClass").html(contentvalue)
        // });
        $(".variant-pill-list.pack-size .variant-pill-list-wrap .variant-pill").hide();
        var selectedPackSize =  localStorage.getItem("selectedcolor");

        availableColorVariantss.forEach((variant, index) => {
        
        $(".variant-pill-list.pack-size .variant-pill-list-wrap .packSizeClass")
          .eq(index)
          .each(function () {
            var _thiss = this;
            var divideInPrice = parseInt($(this).attr("data-divided-by"));

            if (!divideInPrice) return;

            var contentvalue = "Rs " + Math.ceil(variant.price / divideInPrice) + " / pillow";
            console.log("contentvalue======",contentvalue,index);
            $(this).html(contentvalue);
            $(this).parent().parent().show();

            // //added code on 17-12-25 meeting packsize but not seleted bydefault if small clicked
            if(index == 0){
                //$(this).parent().parent().find('input[type="radio"]').trigger('click');
                var radioButton = _thiss.closest('div').querySelector('input[type="radio"]');
                if (radioButton) {
                    radioButton.click();
                }
               _this.activateAddTocartButton();
            }
            // //added code on 17-12-25 meeting packsize but not seleted bydefault if small clicked
            
            console.log("divideInPrice", divideInPrice);
          });
        });
         
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }
    
      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
       
      }

      renderSwatches(data) {
        const swatchValues = data.optionSwatches?.values || [];
        const variants = data.selectedVariants;
        const colorIndex = data.colorOptionIndex;
      
        if (!swatchValues.length || !variants || colorIndex == null) return '';
      
        const renderedColors = new Set();
        let swatchListHTML = ' <span class="selected_val"></span><ul class="color_option_list">';
      
        let isFirstSwatch = true;

          variants.forEach(variant => {
            const colorValue = variant[`option${colorIndex}`];
          
            if (!renderedColors.has(colorValue)) {
              const swatch = swatchValues.find(s => s.value === colorValue);
              if (!swatch) return;
          
              renderedColors.add(colorValue);
          
              let style = '';
              if (swatch.swatch_type === 'image') {
                style = `background-image:url(${swatch.swatch_value});`;
              } else if (swatch.swatch_type === 'color') {
                style = `background-color:${swatch.swatch_value};`;
              }
          
              const activeClass = isFirstSwatch ? 'active' : '';
              isFirstSwatch = false; // Only the first swatch gets 'active'
          
              swatchListHTML += `
                <li 
                  class="color_option ${activeClass}" 
                  style="${style}" 
                  data-variant-id="${variant.id}"
                  title="${colorValue}"
                  data-value="${colorValue}"
                ></li>`;
            }
          });
      
        swatchListHTML += '</ul>';
      
        const variantsJSON = JSON.stringify(variants).replace(/'/g, "&apos;"); // escape single quotes for HTML
      
        return `
          <div class="variant_list" data-options='${variantsJSON}'>
            ${swatchListHTML}
          </div>
        `;
      }

      renderAddOnWrapper(selectedVariantId) {
        if (
          !selectedVariantId ||
          !variant_related_data.hasOwnProperty(selectedVariantId) ||
          !variant_related_data[selectedVariantId]?.related_product
        ) {
          const dd_hide = document.querySelector(".addOnProducts-wrapper");
          if (dd_hide){
            dd_hide.classList.add("hidden");
          }
          // added code on 17-12-25 due to preselect issue hidden of div is not enough.. need to untick th checkbox
          const container = document.querySelector(".addOnProducts-wrapper");
          if (!container) return;
      
          container.innerHTML = ''; // Clear previous content  
          //added code on 17-12-25

         return; // exit if no valid data
        }else{
          const variantData = variant_related_data[selectedVariantId];
          const data = variantData.related_product;
          console.log("data",data);
          console.log("varinats",data.selectedVariants.length);

          const swatchesHTML = this.renderSwatches(data);

          const container = document.querySelector(".addOnProducts-wrapper");
          if (!container) return;
      
          container.innerHTML = ''; // Clear previous content

          const wrapper = document.createElement('div');
          wrapper.classList.add('product_wrapper');
          wrapper.setAttribute('data-addon-id', data.selectedVariants[0]?.id);

          const firstVariant = data.selectedVariants[0];
          if(firstVariant){
              const comparePrice = firstVariant.compare_at_price;
          const salePrice = firstVariant.price;
          const discount = comparePrice && comparePrice > salePrice
            ? Math.round(((comparePrice - salePrice) / comparePrice) * 100)
            : null;

          wrapper.innerHTML = `
            <div class="title_image_wrapper flexbox align-flex-start">
              <input type="checkbox" id="addOnCheckbox" name="addOnProduct"/>
              <label for="addOnCheckbox" class="title_wrapper">
                <div class="addOn_title flexbox">
                  <span class="custom_checkbox flexbox justify-center align-center">
                    <svg class="checkmark_svg" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6.80741 18C7.25636 18 7.61136 17.7999 7.86194 17.4102L17.7285 1.73782C17.9165 1.43242 18 1.20067 18 0.958407C18 0.379147 17.6137 0 17.0395 0C16.6323 0 16.3921 0.136883 16.1416 0.537152L6.76567 15.6091L1.90022 9.18434C1.63922 8.81566 1.38859 8.66822 1.00232 8.66822C0.417634 8.66822 0 9.079 0 9.6583C0 9.90052 0.10442 10.1744 0.30279 10.4272L5.72156 17.3891C6.04525 17.7999 6.35846 18 6.80741 18Z" fill="currentColor"/></svg>
                  </span>
                  <span class="custom_checkbox_text">Add a ${data.title}</span>
                </div>
                <div class="addOn_info">${data.description}</div>
                <div class="price_wrapper flexbox">
                  ${comparePrice ? `<span class="compare_price" data-compare_price="${comparePrice}">+ ₹${(comparePrice / 100).toFixed(0)}</span>` : ''}
                  <span class="sale_price" data-sale_price="${salePrice}">+ ₹${(salePrice / 100).toFixed(0)}</span>
                  ${discount ? `<span class="discount_percentage">(${discount}% OFF)</span>` : ''}
                </div>
              </label>
              <div class="image_wrapper media-box" style="--media-height: 100%;">
                ${firstVariant.image ? `<img src="${firstVariant.image}" loading="lazy">` : ''}
              </div>
            </div>
            <div class="variant_info_wrapper">
              ${swatchesHTML}
            </div>
          `;
          }
        
      
          container.appendChild(wrapper);

          const dd_hide = document.querySelector(".addOnProducts-wrapper.hidden");
          console.log("remove hiddeeeeeeeeeen 2");
          if (dd_hide){
            console.log("remove hiddeeeeeeeeeen 2");
            dd_hide.classList.remove("hidden"); 
          }

          // Add on products on product page
          const colorOptions = document.querySelectorAll(".addOnProducts-wrapper .color_option");
          if(colorOptions.length){
            colorOptions.forEach(option => {
              option.addEventListener("click", function () {
                document.querySelectorAll(".color_option").forEach(optionelem => {optionelem.classList.remove('active');});
                option.classList.add('active');
                const value = this.getAttribute("data-value");
                console.log("value",value);
                const selectedValEl = this.closest(".variant_list").querySelector(".selected_val");
                if (selectedValEl) {
                  selectedValEl.textContent = value;
                }
                
                let variantId = this.getAttribute("data-variant-id"); // Get selected variant ID
                let variantListWrapper = this.closest(".variant_list"); // Get parent variant list
                let productVariants = JSON.parse(variantListWrapper.getAttribute("data-options")); // Get all variants
                        
                let selectedVariant = productVariants.find(variant => variant.id == variantId);
                if (!selectedVariant) return;
                let product_wrapper = this.closest(".product_wrapper");
                if(product_wrapper){
                  product_wrapper.setAttribute("data-addon-id",variantId);
                }
             
                let imageWrapper = this.closest(".product_wrapper").querySelector(".image_wrapper img");
                // console.log("imageWrapper >>>> ",imageWrapper);
                // console.log("selectedVariant >>>> ",selectedVariant);
                // console.log("selectedVariant.featured_image",selectedVariant.image);
                if (imageWrapper && selectedVariant.image) {
                  imageWrapper.src = selectedVariant.image;
                }
            
                // Update the selected variant title
                let selectedVariantTitle = this.closest(".product_wrapper").querySelector(".selected_varinat");
                if (selectedVariantTitle) {
                  selectedVariantTitle.textContent = selectedVariant.title;
                }
            
                // Update the prices
                let priceWrapper = this.closest(".product_wrapper").querySelector(".price_wrapper");
                if (priceWrapper) {
                  let salePriceEl = priceWrapper.querySelector(".sale_price");
                  let comparePriceEl = priceWrapper.querySelector(".compare_price");
                  let discountPercentageEl = priceWrapper.querySelector(".discount_percentage");
              
                  if (salePriceEl) {
                    salePriceEl.setAttribute("data-sale_price",selectedVariant.price);
                    salePriceEl.textContent = `+ ${Shopify.formatMoney(selectedVariant.price, Shopify.money_format).replace(/\.00$/, "")}`;
                  }
              
                  if (comparePriceEl) {
                    if (selectedVariant.compare_at_price && selectedVariant.compare_at_price > selectedVariant.price) {
                      comparePriceEl.setAttribute("data-compare_price",selectedVariant.compare_at_price);
                      comparePriceEl.textContent = `+ ${Shopify.formatMoney(selectedVariant.compare_at_price, Shopify.money_format).replace(/\.00$/, "")}`;
                      comparePriceEl.style.display = "inline";
                    } else {
                      comparePriceEl.style.display = "none";
                    }
                  }
              
                  // Calculate and display the discount percentage
                  if (discountPercentageEl) {
                    if (selectedVariant.compare_at_price && selectedVariant.compare_at_price > selectedVariant.price) {
                      let discount = ((selectedVariant.compare_at_price - selectedVariant.price) * 100 / selectedVariant.compare_at_price).toFixed(0);
                      discountPercentageEl.textContent = `(${discount}% OFF)`;
                    } else {
                      discountPercentageEl.textContent = "";
                    }
                  }
                }
              });
            });
            const selectedColor = document.querySelector(".addOnProducts-wrapper .color_option.active").getAttribute("data-value");
            // console.log("selected Color",selectedColor);
            document.querySelector(".addOnProducts-wrapper .variant_list .selected_val").textContent = selectedColor;
          }
            
          // Add on products on product page
        }
      }

      // show personalization product if variant metafield value is set to true start
      togglePersonalizationBlock(selectedVariantId) {
        const personalizeBlock = document.querySelector(".personalize_product_block");
        if(personalizeBlock){
          const shouldShowPersonalizeBlock = window.variantMetafields?.[selectedVariantId]?.show_personalize_product;
          if (shouldShowPersonalizeBlock) {
            personalizeBlock.classList.remove("hidden");
           
          } else {
            document.querySelector(".edit_personalization_btn").click();
            document.querySelector(".personlize_button_wrapper .remove_btn").click();
            personalizeBlock.classList.add("hidden");
          }
        }
        
        const descriptionBlock = document.querySelector(".show-variant-meta-data");
        if (descriptionBlock){
          const shouldShowdescriptionBlock = window.variantMetafields?.[selectedVariantId]?.show_variant_description;
          if (shouldShowdescriptionBlock){
            descriptionBlock.classList.remove("hidden");
          }else{
            descriptionBlock.classList.add("hidden");
          }
        }
        
      }
      // show personalization product if variant metafield value is set to true end
      
      toggleVariantDescriptionSection(selectedVariantId) {
        const descriptionBlock = document.querySelector(".show-variant-meta-data");
        const descriptionText = document.querySelector(".show-var-meta-desc");

        if (!descriptionBlock || !descriptionText) return;

        // Updated to use consolidated variantMetafields object
        const variantDescription = window.variantMetafields?.[selectedVariantId]?.show_variant_description;
      
        if (variantDescription && variantDescription !== "null") {
          descriptionText.textContent = variantDescription;
          descriptionBlock.classList.remove("hidden");
        } else {
          descriptionText.textContent = "";
          descriptionBlock.classList.add("hidden");
        }
      }

      getSelectedVariant(productInfoNode) {
        // console.log("productInfoNode >>>> ",productInfoNode);
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        const stickyBarButton = document.querySelector(".sticky-addtocart-btn .btn-text");
       
        // Start-----  Custom Dev 
          
        let selectedVariantVal = null;
        if (selectedVariant) {
          try {
            selectedVariantVal = JSON.parse(selectedVariant);
          } catch (error) {
            console.error("Error parsing selectedVariant HTML:", error);
          }
        }
       
        if(selectedVariantVal){
          // console.log("selectedVariantVal",selectedVariantVal);
          const customDropDown = document.querySelector(".dimension-custom-dropdown .custom-dropdown-button-text");
          if(customDropDown){
            const textLabel = customDropDown.textContent.trim().replace(/\s+/g, " ");
            if(textLabel != 'Select a Size'){
              if(selectedVariantVal.available == true){
                stickyBarButton.textContent = 'Add To Cart';
                stickyBarButton.classList.add("available");
                stickyBarButton.classList.remove("sold-out");
              }else{
                stickyBarButton.textContent = 'Notify Me';
                stickyBarButton.classList.add("sold-out");
                stickyBarButton.classList.remove("available");
              }
            } 
          }
          
         
         
          const selectedVariantId = selectedVariantVal.id;
          // console.log("selectedVariantId >>> ",selectedVariantId);
          this.renderAddOnWrapper(selectedVariantId);
          // call to show personalization product
          this.togglePersonalizationBlock(selectedVariantId);
          this.toggleVariantDescriptionSection(selectedVariantId);
        }
         
          const activeHeightElem = document.querySelector(".height_option.active");
          const heightVal = activeHeightElem ? activeHeightElem.getAttribute("data-value") : null;
          
          const stickyBar = document.querySelector(".product-main-sticky-bar");
          const stickyBarVariant = stickyBar.querySelector(".product-sticky-variant");
          const priceElem = stickyBar.querySelector(".main-price");
          const comparePriceElem = stickyBar.querySelector(".compare-price");
          // console.log("selectedVariantVal >>> ",selectedVariantVal);
          if (selectedVariantVal) {
           const formatOption = (option) => {
            if (!option) return option;
          
            const lower = option.toLowerCase();
          
            // Check for "x" for dimensions
            if (lower.includes('x')) {
              return `${option} inches`;
            }
          
            // Check for "pillow" and extract number
            if (lower.includes('pillow')) {
              const match = option.match(/^(\d+)\s*pillow/i);
              if (match) {
                return `pack of ${match[1]}`;
              }
            }
          
            return option;
          };
            
            const parts = [];
            const customOpt = document.querySelector(".product__info-wrapper .variant-pill-list.product-type");
            // console.log("customOpt >>> ",customOpt);
              if (customOpt) {
                const activeLink = customOpt.querySelector("a.active");
                if (activeLink) {
                  const labelText = activeLink.textContent.trim();
                  // console.log("Active Product-Type Label:", labelText);
                  if (labelText) parts.push(labelText);
                }
              }
            const checkedInput = customOpt?.querySelector("input[type='radio']:checked");
            // console.log("checkedinput",checkedInput);
            if (checkedInput) {
              console.log("checkedinput",checkedInput);
              const label = customOpt.querySelector(`label[for="${checkedInput.id}"]`);
              const labelText = label?.textContent.trim() || "";
              console.log("Checked Label:", labelText);
              if(labelText) parts.push(labelText);
            }
            
            if (selectedVariantVal.option1) parts.push(formatOption(selectedVariantVal.option1));
            if (heightVal) parts.push(`${heightVal} inch`);
            if (selectedVariantVal.option2) parts.push(formatOption(selectedVariantVal.option2));
            if (selectedVariantVal.option3) parts.push(formatOption(selectedVariantVal.option3));

            const formattedText = parts.join(' | ');
              // console.log("formattedText",formattedText);
              // stickyBarVariant.textContent = formattedText;
            const dropdownText = document.querySelector('.custom-dropdown-button-text');
            if(dropdownText){
              if (dropdownText.textContent.trim() !== 'Select a Size') {
                stickyBarVariant.textContent = formattedText;
              }
            }else{
              stickyBarVariant.textContent = formattedText;
            }
            
            priceElem.textContent = Shopify.formatMoney(selectedVariantVal.price, Shopify.money_format).replace(/\.00$/, '');
            comparePriceElem.textContent = Shopify.formatMoney(selectedVariantVal.compare_at_price, Shopify.money_format).replace(/\.00$/, '');
            // console.log("select varant get",selectedVariantVal );
          }else{
            const dropdownText = document.querySelector('.custom-dropdown-button-text');
            if(dropdownText){
              if (dropdownText.textContent.trim() == 'Select a Size') {
                const spanElement = document.createElement("span");
                spanElement.textContent = 'Select a Size!';
                spanElement.style.color = 'red';
                stickyBarVariant.replaceChildren(spanElement);
              }
            }
             // console.log("select a size setted" );
          
          }
        // end ------ Custom Dev 

        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }
        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects .product_customDropdown.dimension_data.dimensions');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      updateValuesFunction(productData,variant){
        console.log("***************function updateValuesFunction****************")
        console.log("variantvariantvariantvariant",variant);
        const _this = this;
        var productUrl = "/products/"+productData.handle;
        var variantId  = variant.id;
        //console.log(productData,option1Value,option2Value,variantId);
        $(".product-variant-id").val(variantId);
        //updateURL(productUrl, variant?.id);
        window.history.replaceState({}, '', `${productUrl}${variantId ? `?variant=${variantId}` : ''}`);
        $(".product-form__buttons button").removeAttr("disabled");
        var sku = variant.sku;
        var firstVariPrice = _this.formatINR(variant.price);   
        var firstVari_C_Price = _this.formatINR(variant.compare_at_price); 
        var firstVari_C_Price_n = variant.compare_at_price; 
        console.log("ccccccccccccccc",firstVariPrice,firstVari_C_Price,firstVari_C_Price_n,variant);
        $(".compare_price").attr("data-compare-price",firstVari_C_Price_n);  
        $(".compare_price .price-item.price-item--regular").text(firstVari_C_Price);  
        $(".sticky-pricebox .compare-price").text(firstVari_C_Price);  
        $(".store-price .price-item.price-item--sale.price-item--last").text(firstVariPrice);  
        $(".sticky-pricebox .main-price").text(firstVariPrice);
        $("#pincode").attr("data-sku", sku);
        $("#pincode").attr("data-outofstock", variant.available);// ADDED CODE ON 28-12-25 DUE on notify me delivery timeline is not give reustl
        _this.updateStickyBarForVariant(variantId);
        
        if(variant.available == true){
            _this.updateStickyBarForATCVerifyButton(3); // added code on 20-12-25
        }else if(variant.available == false){
            _this.updateStickyBarForATCVerifyButton(2); // added code on 20-12-25
        }else{
          console.log("*************no atc no.. notify me*************");
        }
        // added code on 27-12-25
        var mediaAlt =  variant?.featured_media?.alt
         _this.cstUpdateMedia(mediaAlt);

        //added code on as follow product has to add iscustom true 29-12-25 
        if(productData.handle == 'mattress-cover' || productData.handle == 'dog-bed-cover' || productData.handle == 'orthopedic-dog-bed-cover'){
          const qty = window.variantInventory[variant.id];
          console.log(qty);
          const pincodeInput = document.querySelector(".pincode-wrapper input[id='pincode']");
          if(qty <= 0){
            
            if(pincodeInput) pincodeInput.setAttribute("data-custom",true);
            _this.removeCustomSizeWhenInventoryNotZero();
             _this.insertCustomSizeWhenInventoryZero()
            
          }else{
             if(pincodeInput) pincodeInput.setAttribute("data-custom",false);
            _this.removeCustomSizeWhenInventoryNotZero();
          }
        }
      }

      insertCustomSizeWhenInventoryZero() {
        console.log("insertCustomSizeIsMto");
        const targetDiv = document.querySelector('.product-form form');
        if (targetDiv) {
          var dimension_value = localStorage.getItem("selectedSize")
          const input = document.createElement('input');
          input.type = 'hidden';
          input.className = 'cover_properties';
          input.name = 'properties[_custom_size]';
          input.value = dimension_value;

          targetDiv.appendChild(input);

        }
      }

    removeCustomSizeWhenInventoryNotZero() {
          console.log("removeCustomSizeIsMto");
          var productForm = document.querySelector(".product-form form");
          if (productForm) {
            // Remove _custom_size input if it exists
            const existingCustomSizeInput = productForm.querySelector('input.cover_properties[name="properties[_custom_size]"]');
            if (existingCustomSizeInput) {
              productForm.removeChild(existingCustomSizeInput);
            }
          }
      }
    

      getOption2Option3value(selectedcolor,selectedSize,option){
          if(option.match("x")){
            return selectedSize;
          }else{
            return selectedcolor
          }
      }

      getSizeColorValue(value1,value2,caseName){
        if(caseName == "s"){
          if(value1 && value1.match("x")){   
            return localStorage.getItem("selectedSize");
          }else if(value2 && value2.match("x")){
             return localStorage.getItem("selectedSize");
          }else{
            console.log("no match of X");
          }
        }
        if(caseName == "c"){
          if(value1 && !value1.match("x")){
            return value1;
          }else if(value2 && !value2.match("x")){
             return value2;
          }else{
            return value1;
          }
        } 
      }

      getVariantByselectedOptionValuesName(selectedOptionValuesName,selectedOptionValuesLength){
        var _this = this;
        console.log("getVariantByselectedOptionValuesName",selectedOptionValuesName);
        const selectedCat = selectedOptionValuesName[0];
        //const selectedSize = selectedOptionValuesName[1];
        //localStorage.getItem("selectedSize")
        const selectedSize = _this.getSizeColorValue(selectedOptionValuesName[1],selectedOptionValuesName[2],"s");
        const selectedcolor = _this.getSizeColorValue(selectedOptionValuesName[1],selectedOptionValuesName[2],"c");
        //localStorage.setItem("selectedCat",selectedCat);
        //var selectedCat =  localStorage.getItem("selectedCat");
        //var selectedcolor =  localStorage.getItem("selectedcolor");
        //var selectedSize =  localStorage.getItem("selectedSize");
        console.log("************",selectedCat);
        console.log("************",selectedSize);
        console.log("************",selectedcolor);
        var productData = window.productData;
        var availableColorVariants = [];
        if(selectedOptionValuesLength == 2){
             console.log("for 2 options")
              availableColorVariants = window.productData.variants        
              .filter(v => v.option1 === selectedCat && v.option2 === selectedSize);
        }else if(selectedOptionValuesLength == 3){
              console.log("for 3 options")
              availableColorVariants = window.productData.variants        
                .filter(v => v.option1 === selectedCat &&
                             v.option2 === _this.getOption2Option3value(selectedcolor,selectedSize,v.option2)  &&
                             v.option3 === _this.getOption2Option3value(selectedcolor,selectedSize,v.option3));
               console.log(availableColorVariants); 
        }else{

          console.log("*****for more the 3 options****");

        }
        console.log(availableColorVariants[0]);
        return availableColorVariants[0];
        
      }

      deactivateAddTocartButton(){
        console.log("*********************deactivateAddTocartButton**********************");
        const defaultBuyButton = document.querySelector(".buy-buttons-block");  
        if(defaultBuyButton) defaultBuyButton.classList.remove("active");
      }

      activateAddTocartButton(){
        console.log("*********************activateAddTocartButton**********************");
        const defaultBuyButton = document.querySelector(".buy-buttons-block");  
        if(defaultBuyButton) defaultBuyButton.classList.add("active");
      }
      updatermMetaText(option){

        var rmMetaText = document.querySelector(".rm-meta-text"); //for bed products Recommended mattress size:
         if(rmMetaText){

            rmMetaText.textContent = "Recommended mattress size: "+option+" inches";
            rmMetaText.style.display = "block";// added code 20-12-25 on liquid file and here

         }

      }
      heightButtonHideShowPerSize(){
        var _this = this;
        console.log("heightButtonHideShowPerSize");
        var selectedSize =  localStorage.getItem("selectedCat");

        var arraySelectedVariantArray = this.getAlltheSelectedValues();
        var dimensionPostion = this.findTheDimensionPostion(arraySelectedVariantArray);
        var selectedcolor =  localStorage.getItem("selectedcolor");;
         var availableColorVariantss;
        if(dimensionPostion == 3){
          availableColorVariantss = window.productData.variants
          /*.filter(v => v.option1 === selectedSize && v.available)*/
          .filter(v => v.option1 === selectedSize && v.option2 === selectedcolor)
          .map(v => ({
                        option1: v.option1,
                        option3: v.option2,
                        option2: _this.optionDimensionValue(v.option2,v.option3),
                        price: v.price/100,
                        compare_at_price: _this.formatINR(v.compare_at_price),
                        compareatprice: v.compare_at_price
                    }));
          console.log("cccccnew",availableColorVariantss);
        }else{
            availableColorVariantss = window.productData.variants
            .filter(v => v.option1 === selectedSize)
            /*.map(v => this.optionDimensionValue(v.option2,v.option3)*/
            .map(v => ({
                        option2: _this.optionDimensionValue(v.option2,v.option3),
                        price: v.price/100,
                        compare_at_price: _this.formatINR(v.compare_at_price),
                        compareatprice: v.compare_at_price
                    })      
            );
        }
        console.log("bvbvvvvvvvvvvvvv",availableColorVariantss);
        const heightSet = new Set();
        availableColorVariantss.forEach((variant, index) => {
          const value = variant.option2.trim(); // "78 x 36 x 10"
          console.log(variant.option2);
          const parts = variant.option2.split("x").map(p => p.trim());
          const height = parts[parts.length - 1]; // Get "10"
          if (height) {
            heightSet.add(height);
          }
        });
         console.log("************heightSet",heightSet);
        var heightOptions = document.querySelectorAll(".product-form__input.height .height_option");
        var savedHeight;
        console.log(heightOptions);
        var count=1;
        heightOptions.forEach(option=> {
          const value = option.getAttribute("data-value");
          console.log(heightSet.has(value));
          if (heightSet.has(value)) {
            //console.log("***********mohitif");
            if(count == 1){
            savedHeight = value;
            }
            option.closest(".variant-pill").classList.remove("hidden");
            option.classList.remove("hidden");
            option.closest(".variant-pill").style.display = "block";
            count++;
          } else {
            //console.log("***********mohitelse");
            option.classList.add("hidden");
            option.closest(".variant-pill").classList.add("hidden");
            option.closest(".variant-pill").style.display = "none";
          }
          //count++;
        });
        console.log("*************savedHeight",savedHeight);
         let savedHeightOption = document.querySelector(`.height_option[data-value="${savedHeight}"]`);
        // document.querySelectorAll('.variant-pill-list-wrap flexbox .variant-pill input[type="radio"]').forEach(radio => {
        //    // radio.checked = false;
            
        // });
        document.querySelectorAll('.variant-pill-list-wrap flexbox .variant-pill .height_option').forEach(elem => {
            elem.classList.remove("active");            
        });
        console.log("savedHeightOption",savedHeightOption);
        if ((savedHeightOption != null)) {
          //console.log(savedHeightOption.closest('div').querySelector('input[type="radio"]'));
          //savedHeightOption.closest('div').querySelector('input[type="radio"]').click();
         console.log("height option triggered...2");

          const firstVisibleOptionn = document.querySelector(".height_option:not(.hidden)");
          //console.log("mohit",firstVisibleOptionn);
          firstVisibleOptionn?.click();
        }
      }

      findTheDimensionPostion(selectedOptionValuesName){

        //const index = selectedOptionValuesName.findIndex(item => item.includes("x"));
        const dimensionPattern = /\d+\s*x\s*\d+/i;
        const index = selectedOptionValuesName.findIndex(
          item => dimensionPattern.test(item)
        );
        console.log(index);
        return parseInt(index+1);
      }

      colorOptionClickToSetDimensionDropDown(selectedOptionValuesName,dimensionPostion){
        var _this = this;
        var element_value = document.querySelector(".product-form__input.height .height_option");
        console.log(" handleUpdateProductInfo",element_value);
        if(element_value !== null){
           var heightselected = document.querySelector(".product-form__input.height .height_option.active").getAttribute('data-value');
        }else{
           var heightselected = document.querySelector(".product-form__input.height .height_option.active");
        }

        console.log("****colorOptionClickToSetDimensionDropDown****");
        console.log("dimensionPostion if",selectedOptionValuesName[1]);
        var selectedSize =  localStorage.getItem("selectedCat");
        var availableColorVariants;

        document.querySelectorAll('.dimension_val').forEach(el => {
            el.classList.add('hidden');
        });
        if(dimensionPostion == 3){
          availableColorVariants = window.productData.variants
            /*.filter(v => v.option1 === selectedSize && v.available)*/
            .filter(v => v.option1 === selectedSize && v.option2 === selectedOptionValuesName[1])
            .map(v => ({
                option2: this.optionDimensionValue(v.option2,v.option3), 
                price: _this.formatINR(v.price),
                compare_at_price: _this.formatINR(v.compare_at_price),
                compareatprice: v.compare_at_price
          }));
        }else{
          availableColorVariants = window.productData.variants
          /*.filter(v => v.option1 === selectedSize && v.available)*/
          .filter(v => v.option1 === selectedSize)
          .map(v => ({
              option2: this.optionDimensionValue(v.option2,v.option3), 
              price: _this.formatINR(v.price),
              compare_at_price: _this.formatINR(v.compare_at_price),
              compareatprice: v.compare_at_price
          }));
        }
        console.log("**************[availableColorVariants]************",availableColorVariants);
        availableColorVariants.forEach(variantname=>{ //first else 
          var dropDownList;
          console.log("else no height ens");
          if(heightselected){
             console.log("else height selected selected",heightselected);
             if(variantname.option2.match("x")){
                dropDownList = document.querySelector(`.dimension_val[data-value="${variantname.option2}"][data-sizevalue="${heightselected}"]`);
             }else{ // added code on 29-12-25 mattress-cover
                dropDownList = document.querySelector(`.dimension_val[data-value="${variantname.option3}"][data-sizevalue="${heightselected}"]`);
             }
             //dropDownList = document.querySelector(`.dimension_val[data-value="${variantname.option2}"][data-sizevalue="${heightselected}"]`);       
          }else{
            console.log("elseeeeeeee else height selected selected",heightselected);
            dropDownList = document.querySelector(`.dimension_val[data-option1="${selectedSize}"][data-value="${variantname.option2}"]`);
            
          }
          console.log("dropDownList**********",dropDownList);
          if(dropDownList){
              dropDownList.classList.remove('hidden');
          }
        });
        const firstVisible = document.querySelector('li.dimension_val:not(.hidden)');//commented on 18-17 for updatermMetaText(option)
        var firstVisibleValue = firstVisible.getAttribute("data-value");
        console.log("**************firstVisibleValue**************",firstVisibleValue);
        firstVisible.click();
        _this.dimension_border_set();
      }

      dimension_border_set(){
        const element = document.querySelector('.custom-dropdown-button');
        element.style.border = '2px solid #b7805a';
        element.style.borderRadius = '5px';
      }

      dimension_border_reset(){
        const element = document.querySelector('.custom-dropdown-button');
        element.style.border = '1px solid #b0aca8';
        element.style.borderRadius = '5px';
      }

      handleUpdateProductInfo(productUrl,selectedOptionValuesLength,clickedelement,selectedOptionValuesName,isColor,isCategory) {
        console.warn("handleUpdateProductInfo");
        var _this =this;
        
        var dimensionPostion = _this.findTheDimensionPostion(selectedOptionValuesName);
        var optionsname = window.productData.options;
        console.log("optionsname",optionsname);
        $('.convert_inch').click(); //added code on 18-12-25 to reset inches in dropdown

        //added code on 18-12-25 to reset notify me to add to cart 
        // const stickyBarButton = document.querySelector(".sticky-addtocart-btn .btn-text");
        // stickyBarButton.textContent = 'Add To Cart';
        // stickyBarButton.classList.add("available");
        // stickyBarButton.classList.remove("sold-out");
       
        //added code on 29-12-25 iscutme need to pass true for dog bed is custom text is added 
        var data_custom = false;
        const personalizeBlock = document.querySelector(".personalize_product_block");
        if(personalizeBlock){
        var result_block = personalizeblock.querySelector(".personalization_result");
        
          if(result_block){
              var result_block_personlization = result_block.getAttribute("personlization");
              console.log("result_block_personlization",result_block_personlization);
              if(result_block_personlization == "true"){
                  data_custom = true;
                  console.log("data_custom",data_custom);
              }
          }
        }
        
        console.log("data_custom",data_custom);

        // added code on 28-12-25 due to daya-custom need tp pass false when category clicked
        const pincodeInput = document.querySelector(".pincode-wrapper input[id='pincode']");
        //if(pincodeInput) pincodeInput.setAttribute("data-custom",false);
        if(pincodeInput) pincodeInput.setAttribute("data-custom",data_custom);
        
        //added code on 18-12-25 to reset notify me to add to cart

        if(clickedelement == 1){// for categories
            _this.dimension_border_reset();
            console.log("category categories listed");
            localStorage.removeItem("selectedSize");

            // added code on 26-12-25 down to up
            const custome_size = document.querySelector(".custom-size");
            if(custome_size !== null){
              custome_size.classList.remove("active");
              localStorage.removeItem("customSize");
            }
            $(".custom-size-wrapper").addClass("hidden");
            $(".product_customDropdown.dimension_data.dimensions").removeClass("hidden");


            //added 24-12-25 due to mis hight data put this upside
            const selectedCat = event.target.value;
            console.log("selectedCatselectedCatselectedCat",selectedCat);
            const dimensionPattern = /\d+\s*x\s*\d+/i;
            if (!dimensionPattern.test(selectedCat)) {
            //if(!selectedCat.includes("x")){
               console.log("set value of cat on load");
               localStorage.setItem("selectedCat",selectedCat);
               _this.heightButtonHideShowPerSize();// commented on 24-12-25 due to some issue
            }
            // by default set the size while click on single so added this
            var element_value = document.querySelector(".product-form__input.height .height_option");
            //console.log("mohitfirst handleUpdateProductInfo",element_value);
            if(element_value !== null){
               var heightselected = document.querySelector(".product-form__input.height .height_option.active").getAttribute('data-value');
            }else{
               var heightselected = document.querySelector(".product-form__input.height .height_option.active");
            }

            console.log("heightselected....",heightselected);
            //document.querySelector(".custom-size").classList.remove('active');
            document.querySelectorAll('.dimension_val').forEach(el => {
              el.classList.add('hidden');
            });
          
            //commented 24-12-25 due to mis hight data put this upside
            // const selectedCat = event.target.value;
            // if(!selectedCat.includes("x")){
            //    console.log("set value of cat on load");
            //    localStorage.setItem("selectedCat",selectedCat);
            //    _this.heightButtonHideShowPerSize();// commented on 24-12-25 due to some issue
            // }
            var selectedSize =  localStorage.getItem("selectedCat");
            console.log("selectedOptionValuesLength",selectedOptionValuesLength);
            var availableColorVariants;
            if(selectedOptionValuesLength == 2){
                availableColorVariants = window.productData.variants
                /*.filter(v => v.option1 === selectedSize && v.available)*/
                .filter(v => v.option1 === selectedSize)
                .map(v => ({
                    option2: v.option2,
                    price: _this.formatINR(v.price),
                    compare_at_price: _this.formatINR(v.compare_at_price),
                    compareatprice: v.compare_at_price
                }));
            }else if(selectedOptionValuesLength == 3){
              // added code on 24-12-25 size with color combination
              if(dimensionPostion == 3){
                  console.log("dimensionPostion if",selectedOptionValuesName[1],dimensionPostion);
                    availableColorVariants = window.productData.variants
                  /*.filter(v => v.option1 === selectedSize && v.available)*/
                  .filter(v => v.option1 === selectedSize && v.option2 === selectedOptionValuesName[1])
                  .map(v => ({
                      option2: this.optionDimensionValue(v.option2,v.option3), 
                      price: _this.formatINR(v.price),
                      compare_at_price: _this.formatINR(v.compare_at_price),
                      compareatprice: v.compare_at_price
                  }));
              }else{
                console.log("dimensionPostion else",selectedOptionValuesName[1],dimensionPostion);
                availableColorVariants = window.productData.variants
                /*.filter(v => v.option1 === selectedSize && v.available)*/
                .filter(v => v.option1 === selectedSize)
                .map(v => ({
                    option2: this.optionDimensionValue(v.option2,v.option3), 
                    price: _this.formatINR(v.price),
                    compare_at_price: _this.formatINR(v.compare_at_price),
                    compareatprice: v.compare_at_price
                }));
              }
            }else{
                availableColorVariants =[];
                console.log("option is higher then 3");
            }
            
            console.log(window.productData.variants);
            console.log("availableColorVariants first");
            console.log(availableColorVariants);
            /*************** added code on 28-11-25*****************/
            // // const productInfoElem = document.querySelector(".custom-dropdown-button-text")// comment due to pillow products it is setting select a size
            // // productInfoElem.removeAttribute("data-selected");
            // // productInfoElem.textContent = "Select a Size";

            // commented code on 26-12-25 down to up
            // const custome_size = document.querySelector(".custom-size");
            // if(custome_size !== null){
            //   custome_size.classList.remove("active");
            //   localStorage.removeItem("customSize");
            // }
            // $(".custom-size-wrapper").addClass("hidden");
            // $(".product_customDropdown.dimension_data.dimensions").removeClass("hidden");
            
            // // const dd_hide = document.querySelector(".addOnProducts-wrapper");
            
            // // if (dd_hide){
            // //   dd_hide.classList.add("hidden");
            // // }
            /************** added code on 28-11-25*****************/
            let obj = {} // added on 17-12-25
            if(custome_size !== null){ 
             
             availableColorVariants.forEach(variantname=>{ //first if
                var dropDownList;
                //console.log("availableColorVariants",variantname);
                if(!heightselected){ // for founders-mattress
                     console.log("no height selected selected",heightselected);
                     dropDownList = document.querySelector(`.dimension_val[data-option1="${selectedSize}"][data-value="${variantname.option2}"]`);
                }else{
                    console.log("height selected selected",heightselected);
                     dropDownList = document.querySelector(`.dimension_val[data-value="${variantname.option2}"][data-sizevalue="${heightselected}"]`);
                    //console.log(dropDownList,variantname);
                    //console.log(`.dimension_val[data-value="${variantname}"][data-sizevalue="${heightselected}"]`);
                }
                
                if(dropDownList){
                  // added code on 17-12-25 when only singal size is avalible for size
                      let key = selectedSize;   // dynamic key
                      let value = variantname.option2;     // dynamic value
                      //(obj[key] ??= []).push(value);
                      (obj[key] ??= []);
                      if (!obj[key].includes(value)) {
                        obj[key].push(value);
                      }
                      //console.log(obj);
                      // added code on 17-12-25 when only singal size is avalible for size

                  dropDownList.classList.remove('hidden');
                }
                
              });
            }else{
              availableColorVariants.forEach(variantname=>{ //first else 
                  var dropDownList;
                  console.log("else no height ens");
                  if(heightselected){
                     console.log("else height selected selected",heightselected);
                     dropDownList = document.querySelector(`.dimension_val[data-value="${variantname.option2}"][data-sizevalue="${heightselected}"]`);
                    
                  }else{
                    console.log("elseeeeeeee else height selected selected",heightselected);
                    dropDownList = document.querySelector(`.dimension_val[data-option1="${selectedSize}"][data-value="${variantname.option2}"]`);
                    console.log("dropDownList**********",dropDownList);
                  }

                  if(dropDownList){
                      // added code on 17-12-25 when only singal size is avalible for size
                      let key = selectedSize;   // dynamic key
                      let value = variantname.option2;     // dynamic value
                      //(obj[key] ??= []).push(value);
                      (obj[key] ??= []);
                      if (!obj[key].includes(value)) {
                        obj[key].push(value);
                      }
                      //console.log(obj);
                      
                      // added code on 17-12-25 when only singal size is avalible for size

                      dropDownList.classList.remove('hidden');
                  }
              });
            }
            console.log("counttttddddddddddddd==",obj);

           // added code on 17-12-25 when only singal size is avalible for size 
          const count = Object.values(obj).flat().length;
          console.log(count); // 3
           const firstVisible = document.querySelector('li.dimension_val:not(.hidden)');//commented on 18-17 for updatermMetaText(option)
           var firstVisibleValue = firstVisible.getAttribute("data-value");
           console.log("**************firstVisibleValue**************",firstVisibleValue);
           _this.updatermMetaText(firstVisibleValue)// added on 18-12-25 for updatermMetaText(option)
           if(count == 1){
              //const firstVisible = document.querySelector('li.dimension_val:not(.hidden)'); commented on 18-17 for updatermMetaText(option)
              //var firstVisibleValue = firstVisible.getAttribute("data-value");
              firstVisible.click(); 
              $(".dropdown-arrow").hide();
              $(".dimension_wrapper").hide();
              console.log("setting size on load");
              localStorage.setItem("selectedSize",firstVisibleValue); 
              _this.dimension_border_set();
              //_this.updateStickyBarForATCVerifyButton(3);   // added code on 20-12-25 due to show atc on sticky
           }else{

            //added code on 18-12-25 to prevet add to cart for single diemnese
            const productInfoElem = document.querySelector(".custom-dropdown-button-text")
            productInfoElem.removeAttribute("data-selected");
            productInfoElem.textContent = "Select a Size";
            //added code on 18-12-25 to prevet add to cart for single diemnese

            MTOMessageManager.reset(); //added code on 29-12-25 as mto msg was nor hidden on click on custom size to std size

            $(".dropdown-arrow").show();
            $(".dimension_wrapper").show();
            _this.updateStickyBarForATCVerifyButton(1);   // added code on 20-12-25 due to show select a size
           } 

        /********"****** on select size and cat dimension is clicked automatically****")*/
        // const firstVisible = document.querySelector('li.dimension_val:not(.hidden)');
        // var firstVisibleValue = firstVisible.getAttribute("data-value");
        // firstVisible.click(); // commentted on 17-12-25
        // console.log("setting size on load");
        // localStorage.setItem("selectedSize",firstVisibleValue);
        /********"****** on select size and cat dimension is clicked automatically****")*/
        
        /********** get the product selected variant */
        // commentted on 17-12-25
        //var availableColorVariantsdata = _this.getVariantByselectedOptionValuesName(selectedOptionValuesName,selectedOptionValuesLength);
        //console.log("ffff",availableColorVariantsdata);
        /*********** upodate the values */
        // commentted on 17-12-25
        //_this.updateValuesFunction(productData,availableColorVariantsdata);

        //start added on 17-12-25
        _this.deactivateAddTocartButton()
        const noOfElement = _this.calculateNofoelements(selectedSize);// added code on 17-12-25 if li element is one the autocalculate the varient etc
        if(noOfElement == 1){
            _this.activateAddTocartButton()
        }
        //end added on 17-12-25

        _this.updatePacksizePrice();// for pillow pack size
          
        /********************1-12-25************* */
            //$(".custom_buy_button.atc-button").addClass("active"); // on category change it gives an error custom button 
            //$(".buy-buttons-block.atc-button").removeClass("active");
        /********************1-12-25************* */
        /********************1-12-25************* */
        //commened on 17-12-meeting due to second list is color was not working
     /* }else if(clickedelement == 2){// for category dimension
        console.log("category dimension listed");
        const selectedDimension = event.target.value;
        localStorage.setItem("selectedDimension",selectedDimension);*/
       //commened on 17-12-meeting due to second list is color was not working  
      }else if(clickedelement == 3 || clickedelement == 2){// for category color
        console.log("category color listed");
        const selectedcolorvalue = event.target.value;
        //console.log("setting color here");
        localStorage.setItem("selectedcolor",selectedcolorvalue);// 12-12-25 commented the code //17-12-25 uncommmnted the code as atsecond position color value is not set mattress-protector
        

        // commentted on 17-12-25
        //var availableColorVariantsdata = _this.getVariantByselectedOptionValuesName(selectedOptionValuesName,selectedOptionValuesLength);
        
        // commentted on 17-12-25
        //_this.updateValuesFunction(productData,availableColorVariantsdata);

        // added code on 17-12-25 for 3 option
        if(clickedelement == 3){
            var availableColorVariantsdata = _this.getVariantByselectedOptionValuesName(selectedOptionValuesName,selectedOptionValuesLength);
            _this.updateValuesFunction(productData,availableColorVariantsdata);
            
            //added code for image chage while click on color 3rd option 18-12-25
            var mediaAlt =  availableColorVariantsdata?.featured_media?.alt
            this.cstUpdateMedia(mediaAlt);
        }

         if(clickedelement == 2){ //added on 17-12-25
           /***********************added code on 19-18-25*********** image was update on 2nd click */
            if(isColor == true){
                _this.dimension_border_reset();
                const firstVisible = document.querySelector('li.dimension_val:not(.hidden)');//commented on 18-17 for updatermMetaText(option)
                var firstVisibleValue = firstVisible.getAttribute("data-value");
                localStorage.setItem("selectedSize",firstVisibleValue);
                _this.colorOptionClickToSetDimensionDropDown(selectedOptionValuesName,dimensionPostion);
                localStorage.removeItem("selectedSize");// added code on 28-12-25 due to dog bed remove it on 2nd click down to up
            }
            console.log("isCategory",isCategory);
            
            if(isCategory == true){
                _this.dimension_border_reset();
                //const firstVisible = document.querySelector('li.dimension_val:not(.hidden)');//commented on 18-17 for updatermMetaText(option)
                //var firstVisibleValue = firstVisible.getAttribute("data-value");
                //localStorage.setItem("selectedSize",firstVisibleValue);
                _this.colorOptionClickToSetDimensionDropDown(selectedOptionValuesName,dimensionPostion);
                //localStorage.removeItem("selectedSize");// added code on 28-12-25 due to dog bed remove it on 2nd click down to up
            }
            

            var availableColorVariantsdata = _this.getVariantByselectedOptionValuesName(selectedOptionValuesName,selectedOptionValuesLength);
            _this.updateValuesFunction(productData,availableColorVariantsdata);
            
            //added code for image chage while click on color 3rd option 18-12-25
            var mediaAlt =  availableColorVariantsdata?.featured_media?.alt
            this.cstUpdateMedia(mediaAlt);
            /***********************added code on 19-18-25*********** image was update on 2nd click */

           //added on 17-12-25
          //_this.deactivateAddTocartButton();// commented due to issue on muslin-throw product is dectivating the atc button

        /*************** added code on 28-11-25 same as clickedelement 1 added here 17-12-25*****************/
            // const productInfoElem = document.querySelector(".custom-dropdown-button-text") // commented on 20-12-25 muslin-throw product set the select a size on single variant
            // //productInfoElem.removeAttribute("data-selected");// commented on 18-12-25 size was not selected
            // productInfoElem.textContent = "Select a Size";
            const custome_size = document.querySelector(".custom-size");
            if(custome_size !== null){
              custome_size.classList.remove("active");
              localStorage.removeItem("customSize");
            }
            $(".custom-size-wrapper").addClass("hidden");
            $(".product_customDropdown.dimension_data.dimensions").removeClass("hidden");
            // const dd_hide = document.querySelector(".addOnProducts-wrapper");
            
            // if (dd_hide){
            //   dd_hide.classList.add("hidden");
            // }
          /*************** added code on 28-11-25 same as clickedelement 1 added here 17-12-25*****************/
          //added on 17-12-25
             //localStorage.removeItem("selectedSize");// commented code on 28-12-25 due to dog bed remove it on 2nd click down to up
         } 
         

      }else{// for unlisted category
          console.log("category is not listed");
      }
      /***************start added code on 12-12-25************ */
      const delpicMsg = document.querySelector(".delivery_input_main span#delpicMsg");
      if (delpicMsg) {
        delpicMsg.innerHTML = "";
      }
      /***************end added code on 12-12-25************ */
        return (html) => {
           //console.log("mohitddddd",html);
          var variant = this.getSelectedVariant(html);
          // if(variant == null){
          //   variant = variant_data;
          // }
          this.pickupAvailability?.update(variant);
          //this.updateOptionValues(html);
          //this.updateURL(productUrl, variant?.id);
          //this.updateVariantInputs(variant?.id);

          
          if (!variant) {
            console.log("variant not avvailable");
            // setTimeout(()=>{
             // alert("hello");
              //const avaible_dimension = document.querySelector('.variant-pill-list.dimension .variant-pill.quantity-available input');
              //if(avaible_dimension){
              //  //console.log("avaible_dimension",avaible_dimension);
              //  avaible_dimension?.click();
              //}else{
              //  // const firstVisible = document.querySelector('li.dimension_val:not(.hidden)');
              //  // firstVisible.setAttribute("selected","selected");
              //  // firstVisible.classList.add("selected");
              //  // const firstVisibleValue = firstVisible.getAttribute("data-value");
              //  const available = document.querySelector('.variant-pill-list.dimension input:not(.disabled)');
              //  //const available = document.querySelector(`.variant-pill-list.dimension input[value="${firstVisibleValue}"]`)
              //  //console.log("available",available); 
              //  available.click();
              //  // const productInfoElem = document.querySelector(".custom-dropdown-button-text")
              //  // productInfoElem.setAttribute("data-selected",firstVisibleValue);
              //  // productInfoElem.textContent = firstVisibleValue;
              //}

              //document.querySelector(".variant-pill-list.pack-size input")?.click();  
            // },500);

            
           //document.querySelector(".variant-pill-list.pack-size input:not(.disabled)")?.click(); 

           // this.setUnavailable();
            return;
          }
          console.log("Variant",variant);
          
          this.updateSaleRibbon(variant);
         

          // this.updateMedia(html, variant?.featured_media?.id);
          var mediaAlt =  variant?.featured_media?.alt
          this.cstUpdateMedia(mediaAlt);
           
          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            //html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            //window.variantStrings.soldOut

             html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.addToCart
         );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
          // localStorage.removeItem("customSize");
          // console.log('variant sku',variant.sku);
          // const delpicMsg = document.querySelector(".delivery_input_main span#delpicMsg");
          // if (delpicMsg) {
          //   delpicMsg.innerHTML = "";
          // }
          
          // const pincodeInput = document.querySelector(".pincode-wrapper input#pincode");
          // //AK custom code
          
          // if (pincodeInput) {
          //    pincodeInput.setAttribute("data-sku",variant.sku);
          // }
          // // console.log("variant",variant);
          // if(variant.available){
          //   if (pincodeInput) {
          //     pincodeInput.setAttribute("data-outofstock",true)
          //   }
          // }else{
          //   if (pincodeInput) {
          //     pincodeInput.setAttribute("data-outofstock",false)
          //   }
          // }
          //End AK custom code

          // CUSTOM DEV
              //this.sizeElems();
              let isHeightOptActive = false;
              // this.customSize();
              var defaultBuyButton = document.querySelector(".buy-buttons-block");
              const stickyBarButton = document.querySelector(".sticky-addtocart-btn .btn-text");
              var customSizeDropdown = document.querySelector(".dimension-custom-dropdown");
              const quantityButtons = document.querySelectorAll(".quantity__button");
              if(customSizeDropdown){
              // Restore the selected size after re-rendering
              const savedSize = localStorage.getItem("selectedSize");
    const heightOption = document.querySelector(".variant-pill-list.height");

    if (savedSize) {
      // VALIDATION: Check if saved dimension exists and is visible for current color
      const savedDimensionEl = customSizeDropdown.querySelector(
        `.dimension_val[data-value="${savedSize}"]:not(.hidden)`
      );

      if (savedDimensionEl) {
        // VALID: Dimension exists for current color - restore it
        const productInfoElem = customSizeDropdown.querySelector(".custom-dropdown-button-text");
        if (productInfoElem) {
          quantityButtons.forEach(qtyBtn=>{
            qtyBtn.classList.remove('disabled');
            qtyBtn.removeAttribute('disabled','disabled');
          });
          productInfoElem.textContent = savedSize + ' inches';
          defaultBuyButton.classList.add('active');
          productInfoElem.setAttribute("data-selected", savedSize);
        }
      } else {
        // INVALID: Dimension doesn't exist for current color
        // Clear stale localStorag

        // For non-height products: server default is already correct in DOM
        // Just ensure buy button is enabled
        if (!heightOption) {
          defaultBuyButton.classList.add('active');
          quantityButtons.forEach(qtyBtn=>{
            qtyBtn.classList.remove('disabled');
            qtyBtn.removeAttribute('disabled','disabled');
          });
        }
        // For height products: will be handled by height restoration logic below
      }
    }
              }
         
              const savedHeight = localStorage.getItem("selectedHeight");
              if (savedHeight) {
                // console.log("Saved Height",savedHeight);
                let savedHeightOption = document.querySelector(`.height_option[data-value="${savedHeight}"]`);
                if (savedHeightOption && !savedHeightOption.classList.contains("active")) {
                  // console.log("savedHeightOption",savedHeightOption);
                  savedHeightOption.dataset.trigger = "true"; // Dynamically set the attribute
                  savedHeightOption.click(); // Simulate the click
                  isHeightOptActive = true;
                  // console.log("height option triggered...1");

                  // ─────────────────────────────────────────────────────────────────────
                  // BUGFIX: Re-attempt savedSize restoration AFTER height is clicked
                  // The initial savedSize check (lines 2331-2348) runs BEFORE height click,
                  // so dimensions for the correct height might not be visible yet.
                  // After height click, hideDimension() shows correct dimensions.
                  // ─────────────────────────────────────────────────────────────────────
                  setTimeout(() => {
                    const savedSize = localStorage.getItem("selectedSize");
                    if (savedSize) {
                      const customSizeDropdown = document.querySelector(".dimension-custom-dropdown");
                      const savedDimensionEl = customSizeDropdown?.querySelector(
                        `.dimension_val[data-value="${savedSize}"]:not(.hidden)`
                      );
                      const productInfoElem = customSizeDropdown?.querySelector(".custom-dropdown-button-text");

                      // Only restore if dropdown still shows "Select a Size" (wasn't already restored)
                      if (savedDimensionEl && productInfoElem && productInfoElem.textContent.trim() === 'Select a Size') {
                        // Dimension is now visible after height change - restore dropdown UI
                        productInfoElem.textContent = savedSize + ' inches';
                        productInfoElem.setAttribute("data-selected", savedSize);
                        defaultBuyButton?.classList.add('active');
                        quantityButtons.forEach(qtyBtn => {
                          qtyBtn.classList.remove('disabled');
                          qtyBtn.removeAttribute('disabled');
                        });

                        // Mark the dropdown list item as selected (NOT the radio - avoid AJAX loop)
                        customSizeDropdown.querySelectorAll('.dimension_val').forEach(el => el.classList.remove('selected'));
                        savedDimensionEl.classList.add('selected');

                        console.log('[handleUpdateProductInfo] Restored savedSize after height click:', savedSize);
                      }
                    }
                  }, 50); // Small delay to ensure hideDimension() has completed
                }
              }

              // Add a delay before resetting the "Select a Size" text
              setTimeout(() => {
                document.querySelectorAll(".category .variant-pill, .choose-your-type .variant-pill").forEach(variantPill => {
                  variantPill.addEventListener("click", function () {
                    const atcBtns = document.querySelectorAll(".atc-button");
                    const stickyBar = document.querySelector(".product-main-sticky-bar");
                    if(atcBtns.length){
                      atcBtns.forEach(atcBtn => {
                        if(atcBtn.classList.contains('hidden')){
                          atcBtn.classList.remove('hidden');
                        }
                      });
                      stickyBar.classList.remove('hidden')
                    }

                  console.log('[HOOK 7] Category pill clicked, exiting custom size mode');

                  const customSizeOpt = document.querySelector(".custom-size");
                  if(customSizeOpt) customSizeOpt.classList.remove('active');
                  localStorage.removeItem("customSize");

                  // console.log("variantPill >>>> ",variantPill);
                  const productInfoElem = document.querySelector(".custom-dropdown-button-text");
                  if (productInfoElem) {
                    const heightOption = document.querySelector(".variant-pill-list.height");
                    if(heightOption){
                      productInfoElem.removeAttribute("data-selected");
                      productInfoElem.textContent = "Select a Size";
                      quantityButtons.forEach(qtyBtn=>{
                        qtyBtn.classList.add('disabled');
                        qtyBtn.setAttribute('disabled','disabled');
                      });

                      // ─────────────────────────────────────────────────────────────
                      // PROPER STATE RESET: Sync ALL dimension state sources
                      // This ensures radio state matches dropdown "Select a Size"
                      // ─────────────────────────────────────────────────────────────

                      // 1. Uncheck all dimension radios (so next click triggers change event)
                      document.querySelectorAll('.dimension .variant-pill input[type="radio"]').forEach(radio => {
                        //radio.checked = false;
                      });

                      // 2. Clear selected class from dropdown list items
                      document.querySelectorAll('.dimension-custom-dropdown .dimension_val').forEach(item => {
                        item.classList.remove('selected');
                      });

                      console.log('[HOOK 7] Reset dimension radios and dropdown selection');

                      defaultBuyButton.classList.remove('active');
                      stickyBarButton.textContent = 'Select Size';
                    }else{
                       defaultBuyButton.classList.add('active');
                    }

                    const dd_hide = document.querySelector(".addOnProducts-wrapper");
                    if (dd_hide){
                      dd_hide.classList.add("hidden");
                    }

                      localStorage.removeItem("selectedSize");

                    // ─────────────────────────────────────────────────────────────
                    // HOOK 7: Category change exits custom size mode
                    // Immediately reset MTO message (Hook 5 will confirm on AJAX complete)
                    // ─────────────────────────────────────────────────────────────
                    console.log('[HOOK 7] Calling MTOMessageManager.reset()');
                    MTOMessageManager.reset();
                  } else {
                    console.log('[HOOK 7] productInfoElem not found!');
                  }
                });
              });
            }, 100); // 100ms delay (adjust if needed)
          setTimeout(()=>{
             this.sizeConverter();
          },500);
            // CUSTOM DEV
          
            setTimeout(()=>{
              const visibleDimensionInputs = document.querySelectorAll(
                '.dimension .variant-pill:not(.hidden) input[type="radio"]'
              );
      
              // console.log("visibleDimensionInputs",visibleDimensionInputs);
              // 2. Extract unique height values from dimension inputs
              const heightSet = new Set();
            
              visibleDimensionInputs.forEach(input => {
                const value = input.value.trim(); // "78 x 36 x 10"
                const parts = value.split("x").map(p => p.trim());
                const height = parts[parts.length - 1]; // Get "10"
                if (height) {
                  heightSet.add(height);
                }
              });
              // console.log("heightSet",heightSet);
              var heightOptions = document.querySelectorAll(".product-form__input.height .height_option");
              heightOptions.forEach(option => {
                const value = option.getAttribute("data-value");
                if (heightSet.has(value)) {
                  option.closest(".variant-pill").classList.remove("hidden");
                  option.classList.remove("hidden");
                } else {
                  option.classList.add("hidden");
                  option.closest(".variant-pill").classList.add("hidden");
                }
              });
              
              let savedHeightOption = document.querySelector(`.height_option[data-value="${savedHeight}"]`);
            
              // console.log("savedHeightOption",savedHeightOption);
              if ((savedHeightOption === null || savedHeightOption.classList.contains("hidden")) && !isHeightOptActive) {
                const firstVisibleOption = document.querySelector(".height_option:not(.hidden)");
                firstVisibleOption?.click();
                // console.log("height option triggered...2");
              }
              
              // If some height option was already marked active
              if (isHeightOptActive) {
                const activeOption = document.querySelector(".height_option.active");
              
                if (activeOption && activeOption.classList.contains("hidden")) {
                  // Active option is hidden — fallback to first visible one
                  const firstVisibleOption = document.querySelector(".height_option:not(.hidden)");
                  if (firstVisibleOption) {
                    firstVisibleOption.click();
                    console.log("height option triggered...3 — active was hidden");
                  }
                }
              }

              const dropdownTextElem = document.querySelector(".custom-dropdown-button .custom-dropdown-button-text");
              const dropdownText = dropdownTextElem?.textContent?.trim(); // BUGFIX: Added .trim() for whitespace
              // console.log("dropdownText",dropdownText);
              if(variant.available){
                if(dropdownText && dropdownText === 'Select a Size'){
                  stickyBarButton.textContent = 'Select a Size';
                }else{
                  stickyBarButton.textContent = 'Add To Cart';
                }
              }else{
                stickyBarButton.textContent = 'Notify Me';
              }

              // ─────────────────────────────────────────────────────────────────
              // HOOK 5 (PRIMARY): Update MTO message after AJAX completes
              // This is the main hook point for variant selection changes
              // ─────────────────────────────────────────────────────────────────
              const selectedVariantInput = document.querySelector('product-form input[name="id"]');
              const selectedVariantId = selectedVariantInput?.value;

              if (selectedVariantId && dropdownText && dropdownText !== 'Select a Size') {
                // Variant is selected - check MTO status
                MTOMessageManager.updateForVariant(selectedVariantId);
              } else {
                // No variant selected (dropdown shows "Select a Size")
                MTOMessageManager.reset();
              }

            },500);
           document.body.classList.remove("variant-loader");
        };
        
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      updateSaleRibbon(variant) {
  const ribbonElement = document.querySelector('#saleribbon');
  const discountElement = document.querySelector('.ribbon-discount-pc');

  if (!ribbonElement || !discountElement || !variant) return;

  if (variant.compare_at_price && variant.compare_at_price > variant.price) {
    const discountPrice = variant.compare_at_price - variant.price;
    const discountPercentage = Math.floor((discountPrice * 100) / variant.compare_at_price);

    discountElement.textContent = discountPercentage + '%';
    ribbonElement.style.display = '';
  } else {
    ribbonElement.style.display = 'none';
  }
}

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        // const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
        //   .map((id) => `#${id}-${this.dataset.section}`)
        //   .join(', ');
        const selectors = ['Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      // default function is commented
      updateMedia(html, variantFeaturedMediaId) {
       if (!variantFeaturedMediaId) return;      
        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      cstUpdateMedia(data_media_alt){
        console.log("**********cstUpdateMedia***********")
        // Skip gallery updates during auto-select to prevent conflicts

        if (this._autoSelecting) return;
        // console.log("data media alt",data_media_alt);
        let mainSlider, thumbnailSlider; // Declare Swiper instances globally
        const zoomView = document.getElementById('zoomView');

        function updateZoomFromActiveSlide() {
          // const zoomView = document.getElementById('zoomView');
          if(zoomView){
             // Optional: CSS hidden initially
            zoomView.style.display = 'none';
            document.addEventListener('mouseover', function (e) {
              if (e.target instanceof Element && e.target.closest('.main-slider .swiper-slide-active img')) {
                const activeImg = e.target;
                const zoomImg = activeImg.getAttribute('data-zoom') || activeImg.src;
            
                zoomView.style.backgroundImage = `url(${zoomImg})`;
                zoomView.classList.remove("hidden");
                zoomView.style.display = 'block';
                
            
                function handleMouseMove(ev) {
                  const rect = activeImg.getBoundingClientRect();
                  const x = ev.clientX - rect.left;
                  const y = ev.clientY - rect.top;
                  const xPercent = (x / rect.width) * 100;
                  const yPercent = (y / rect.height) * 100;
            
                  zoomView.style.backgroundPosition = `${xPercent}% ${yPercent}%`;
                }
            
                function handleMouseLeave() {
                  zoomView.style.display = 'none';
                  zoomView.style.backgroundImage = '';
                  activeImg.removeEventListener('mousemove', handleMouseMove);
                  activeImg.removeEventListener('mouseleave', handleMouseLeave);
                }
                activeImg.addEventListener('mousemove', handleMouseMove);
                activeImg.addEventListener('mouseleave', handleMouseLeave);
              }
            }, true);
          }
        }
        
        // function mainPDPSlider() {
        //     let mainSlider, thumbnailSlider; 
        //   if (document.querySelector('.main-slider')?.swiper) {
        //     document.querySelector('.main-slider').swiper.destroy(true, true);
        //   }
        //   if (document.querySelector('.thumbnail-slider')?.swiper) {
        //     document.querySelector('.thumbnail-slider').swiper.destroy(true, true);
        //   }
      
        //   const totalThumbSlides = document.querySelectorAll('.thumbnail-slider .swiper-slide').length;
      
        //   function getCurrentSlidesPerView() {
        //     const width = window.innerWidth;
        //     if (width >= 1260) return 5;
        //     if (width >= 1080) return 5;
        //     if (width >= 575) return 4;
        //     return 3;
        //   }
      
        //   const currentSlidesPerView = getCurrentSlidesPerView();
        //   const useLoopAndCentering = totalThumbSlides > currentSlidesPerView;
      
        //   mainSlider = new Swiper('.main-slider', {
        //     slidesPerView: 1,
        //     spaceBetween: 0,
        //     autoHeight: true,
        //     loop: useLoopAndCentering,
        //     pagination: {
        //       el: '.product-hero-section .swiper-pagination',
        //       clickable: true
        //     },
        //     navigation: {
        //       nextEl: '.product-hero-section .swiper-button-next',
        //       prevEl: '.product-hero-section .swiper-button-prev',
        //     }
        //   });
      
        //   thumbnailSlider = new Swiper('.thumbnail-slider', {
        //     slidesPerView: 3,
        //     spaceBetween: 10,
        //     slideToClickedSlide: true,
        //     loop: useLoopAndCentering,
        //     centeredSlides: useLoopAndCentering,
        //     centeredSlidesBounds: useLoopAndCentering,
        //     watchSlidesProgress: true,
        //     watchSlidesVisibility: true,
        //     breakpoints: {
        //       575: { slidesPerView: 4 },
        //       1080: { slidesPerView: 5 },
        //       1260: { slidesPerView: 5 }
        //     }
        //   });
      
        //   // Force initial sync
        //   const initialIndex = 0;
      
        //   if (useLoopAndCentering) {
        //     mainSlider.slideToLoop(initialIndex, 0, false);
        //     thumbnailSlider.slideToLoop(initialIndex, 0, false);
        //     const mediaId = document.querySelector(".pdp-main-slider-wrap .swiper-slide.swiper-slide-active")?.getAttribute("data-media-id");
        //     const matchedElem = document.querySelector(`.product-gallery__thumbnail-list [data-media-id="${mediaId}"]`);
        //    console.log("matchedElem",matchedElem);
        //      let matchedIndex = -1;
        //       thumbnailSlider.slides.forEach((slide, index) => {
        //         if(slide.getAttribute("data-media-id") == mediaId){
        //           matchedIndex = index;
        //         }
        //       });
        //     console.log("matchedIndex",matchedIndex);

           
      
        //     // Fix active state
        //     thumbnailSlider.slides.forEach(slide => {
        //       const slideIndex = parseInt(slide.getAttribute('data-swiper-slide-index'));
        //       slide.classList.toggle('swiper-slide-active', slideIndex === initialIndex);
        //     });
      
        //     thumbnailSlider.on('click', (swiper) => {
        //       const clickedSlide = swiper.clickedSlide;
        //       if (!clickedSlide) return;
        //       const realIndex = parseInt(clickedSlide.getAttribute('data-swiper-slide-index'));
        //       if (!isNaN(realIndex)) {
        //         mainSlider.slideToLoop(realIndex);
        //       }
        //     });
      
        //     mainSlider.on('slideChange', () => {
        //       const realIndex = mainSlider.realIndex;
        //       thumbnailSlider.slideToLoop(realIndex);
        //       updateThumbnailActiveClass(realIndex);
        //     });
      
        //     function updateThumbnailActiveClass(realIndex) {
        //       thumbnailSlider.slides.forEach((slide) => {
        //         const slideIndex = parseInt(slide.getAttribute('data-swiper-slide-index'));
        //         slide.classList.toggle('swiper-slide-active', slideIndex === realIndex);
        //       });
        //     }
      
        //   } else {
        //     mainSlider.slideTo(initialIndex, 0, false);
        //     thumbnailSlider.slideTo(initialIndex, 0, false);
      
        //     thumbnailSlider.slides.forEach((slide, index) => {
        //       slide.classList.toggle('swiper-slide-active', index === initialIndex);
        //     });
      
        //     thumbnailSlider.on('click', (swiper) => {
        //       const clickedIndex = swiper.clickedIndex;
        //       mainSlider.slideTo(clickedIndex);
        //       updateThumbnailActiveClassSimple(clickedIndex);
        //     });
      
        //     mainSlider.on('slideChange', () => {
        //       const activeIndex = mainSlider.activeIndex;
        //       thumbnailSlider.slideTo(activeIndex);
        //       updateThumbnailActiveClassSimple(activeIndex);
        //     });
      
        //     function updateThumbnailActiveClassSimple(activeIndex) {
        //       thumbnailSlider.slides.forEach((slide, index) => {
        //         slide.classList.toggle('swiper-slide-active', index === activeIndex);
        //       });
        //     }
        //   }
        // }
        function mainPDPSlider() {
          console.log("************mainPDPSlider**********");
          const mainSliderEl = document.querySelector('.main-slider');
          const thumbSliderEl = document.querySelector('.thumbnail-slider');
        
          if (mainSliderEl?.swiper) mainSliderEl.swiper.destroy(true, true);
          if (thumbSliderEl?.swiper) thumbSliderEl.swiper.destroy(true, true);
        
          const mainSlider = new Swiper('.main-slider', {
            slidesPerView: 1,
            spaceBetween: 0,
            autoHeight: true,
            pagination: {
              el: '.product-hero-section .swiper-pagination',
              clickable: true
            },
            navigation: {
              nextEl: '.product-hero-section .swiper-button-next',
              prevEl: '.product-hero-section .swiper-button-prev',
            }
          });
        
          const thumbnailSlider = new Swiper('.thumbnail-slider', {
            slidesPerView: 3,
            spaceBetween: 10,
            slideToClickedSlide: false,
            breakpoints: {
              575: {
                spaceBetween: 10,
                slidesPerView: 4
              },
              1081: {
                spaceBetween: 20,
                slidesPerView: 5
              }
            }
          });
        
          function updateThumbnailActiveClass(activeIndex) {
            thumbnailSlider.slides.forEach((slide, index) => {
              const isActive = index === activeIndex;
              slide.classList.toggle('swiper-slide-active', isActive); // add/remove swiper-slide-active
              slide.classList.toggle('slide-active', isActive);        // add/remove custom slide-active
            });
        
            const offsetIndex = Math.max(activeIndex - 1, 0);
            thumbnailSlider.slideTo(offsetIndex);
          }
        
          thumbnailSlider.on('click', (swiper) => {
            const clickedIndex = swiper.clickedIndex;
            mainSlider.slideTo(clickedIndex);
            updateThumbnailActiveClass(clickedIndex);
          });
        
          mainSlider.on('slideChange', () => {
            const activeIndex = mainSlider.activeIndex;
            updateThumbnailActiveClass(activeIndex);
          });
        
          // Initial sync
          updateThumbnailActiveClass(mainSlider.activeIndex);
        }




  
        // console.log("data_media_alt",data_media_alt);
    
        
          let matchFound = false;

          if (data_media_alt) {
            const altValues = data_media_alt.split('\n').map(str => str.trim().toLowerCase()); // Break into lines
            // console.log("altValues >>> ",altValues);
            const elements = document.querySelectorAll("[data-media-alt]");
            elements.forEach(element => {
              const dataValue = element.getAttribute("data-media-alt")?.trim().toLowerCase();
         
              // Check if any alt value matches this element's data-media-alt (fully or partially)
              const isMatch = altValues.some(alt => dataValue.includes(alt));
       
              if (!dataValue.includes('all')) {
                if (isMatch) {
                  matchFound = true;
                  element.classList.remove("non-swiper-slide", "hidden");
                  element.classList.add("swiper-slide");
                } else {
                  element.classList.add("non-swiper-slide", "hidden");
                  element.classList.remove("swiper-slide");
                }
              }
            });
            // mainPDPSlider();
            // console.log("matchfound >> ",matchFound);
          }
        
        setTimeout(function(){
          mainPDPSlider();
          if(zoomView){
            updateZoomFromActiveSlide();
          }
        },50);

        let youtubePlayers = {};

        function loadYouTubeAPI() {
          if (window.YT && YT.Player) {
            initializeYouTubePlayers();
          } else {
            const tag = document.createElement("script");
            tag.src = "https://www.youtube.com/iframe_api";
            document.head.appendChild(tag);
            window.onYouTubeIframeAPIReady = initializeYouTubePlayers;
          }
        }
      
        function initializeYouTubePlayers() {
          document.querySelectorAll(".youtube-lazy").forEach(wrapper => {
            const playButton = wrapper.querySelector(".youtube-play-button");
            const videoId = wrapper.dataset.videoId;
            const wrapperId = wrapper.id;
      
            if (!playButton || !videoId || !wrapperId) return;
      
            playButton.addEventListener("click", function () {
              wrapper.innerHTML = `<div id="${wrapperId}-player"></div>`;
      
              youtubePlayers[wrapperId] = new YT.Player(`${wrapperId}-player`, {
                videoId: videoId,
                playerVars: {
                  autoplay: 1,
                  rel: 0,
                  modestbranding: 1,
                  controls: 1
                },
                events: {
                  onStateChange: (event) => {
                    const player = event.target;
      
                    // ✅ Handle video END
                    if (event.data === YT.PlayerState.ENDED) {
                      restorePoster(wrapper, videoId, wrapperId);
                    }
      
                    // ✅ Handle PAUSE (center click or play/pause)
                    if (event.data === YT.PlayerState.PAUSED) {
                      setTimeout(() => {
                        const state = player.getPlayerState();
                        const currentTime = player.getCurrentTime();
                        const duration = player.getDuration();
      
                        const nearStart = currentTime < 1;
                        const nearEnd = currentTime > duration - 1;
      
                        // Restore if still paused after short time
                        if (state === YT.PlayerState.PAUSED && (nearStart || nearEnd || true)) {
                          restorePoster(wrapper, videoId, wrapperId);
                        }
                      }, 500);
                    }
                  }
                }
              });
            });
          });
        }
      
        function restorePoster(wrapper, videoId, wrapperId) {
          wrapper.innerHTML = `
            <div class="youtube-poster flexbox align-center justify-center" style="background-image: url('https://img.youtube.com/vi/${videoId}/hqdefault.jpg')">
              <button class="youtube-play-button yt-lazyload-playbtn" aria-label="Play Video">
                <svg class="icon icon--pdp_youtube_play_icon " width="68" height="48" viewBox="0 0 68 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M66.52 7.74C65.74 4.81 64.03 2.33 61.1 1.55C55.79 0.13 34 0 34 0C34 0 12.21 0.13 6.9 1.55C3.97 2.33 2.27 4.81 1.48 7.74C0.0600001 13.05 0 24 0 24C0 24 0.0600001 34.95 1.48 40.26C2.26 43.19 3.97 45.67 6.9 46.45C12.21 47.87 34 48 34 48C34 48 55.79 47.87 61.1 46.45C64.03 45.67 65.74 43.19 66.52 40.26C67.94 34.95 68 24 68 24C68 24 67.94 13.05 66.52 7.74Z" fill="currentColor"></path>
                  <path d="M45 24L27 14V34" fill="white"></path>
                </svg>
              </button>
            </div>
          `;
          initializeYouTubePlayers(); // Rebind play button
        }
        loadYouTubeAPI();
        document.addEventListener("DOMContentLoaded", loadYouTubeAPI);
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner')?.classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner')?.classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects .product_customDropdown.dimension_data.dimensions');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}